import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { aiApi } from "../../api/aiApi";
import api from "../../../../shared/api/apiClient";
import { useAcademy } from "../../../academy/AcademyContext";
import "./PersonalDetails.css";

const getApiErrorText = (error, fallback = "") => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .join(", ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const normalizeTelegramUsername = (value) =>
  String(value || "")
    .trim()
    .replace(/^https?:\/\/(?:www\.)?t\.me\//i, "")
    .replace(/^t\.me\//i, "")
    .replace(/^@+/, "")
    .split(/[/?#]/, 1)[0]
    .trim();

const formatTelegramUsername = (value) => {
  const username = normalizeTelegramUsername(value);
  return username ? `@${username}` : "--";
};

function formatDate(value) {
  if (!value) return "--";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}

function formatDateShort(value) {
  if (!value) return "--";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString();
  } catch {
    return String(value);
  }
}

function getPrimaryEmail(contact) {
  if (!contact) return null;

  if (Array.isArray(contact.emails) && contact.emails.length > 0) {
    const main = contact.emails.find(
      (e) => (e.status || "").toLowerCase() === "main",
    );
    return (main && main.email) || contact.emails[0].email || null;
  }

  return contact.email || null;
}

function getPrimaryPhone(contact) {
  if (!contact) return null;

  if (Array.isArray(contact.phones) && contact.phones.length > 0) {
    const main = contact.phones.find(
      (p) => (p.status || "").toLowerCase() === "main",
    );
    return (main && main.phone) || contact.phones[0].phone || null;
  }

  return contact.phone || null;
}

function compactText(value, max = 86) {
  const clean = String(value || "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return "--";
  return clean.length <= max ? clean : `${clean.slice(0, max).trim()}...`;
}

function extractTaskDetailValue(description, label) {
  const text = String(description || "");
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const labels = [
    "Telegram chat id",
    "Telegram username",
    "Phone",
    "Order description",
    "Delivery address",
    "Delivery date",
    "Delivery time",
  ];
  const otherLabels = labels
    .filter((item) => item !== label)
    .map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
  const regex = new RegExp(
    `${escaped}:\\s*([\\s\\S]*?)(?=\\n(?:${otherLabels}):|$)`,
    "i",
  );
  const match = text.match(regex);
  return match?.[1]?.trim() || "";
}

function getTaskShortDescription(task) {
  const description = task?.description || "";
  const orderDescription = extractTaskDetailValue(
    description,
    "Order description",
  );
  const deliveryAddress = extractTaskDetailValue(
    description,
    "Delivery address",
  );
  const deliveryDate = extractTaskDetailValue(description, "Delivery date");
  const deliveryTime = extractTaskDetailValue(description, "Delivery time");
  const parts = [
    orderDescription,
    deliveryAddress ? `Address: ${deliveryAddress}` : "",
    deliveryDate || deliveryTime
      ? `${deliveryDate} ${deliveryTime}`.trim()
      : "",
  ].filter(Boolean);
  return compactText(parts.length ? parts.join(" · ") : description, 90);
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (["won", "done", "completed", "success"].includes(s))
    return "pdBadgeSuccess";
  if (["lost", "overdue", "failed", "cancelled"].includes(s))
    return "pdBadgeDanger";
  if (["qualified", "proposal", "open", "contacted"].includes(s))
    return "pdBadgeInfo";
  return "pdBadgeWarning";
}

const LEAD_WORKSPACE_STATUSES = [
  "new",
  "contacted",
  "in_work",
  "in_deal",
  "won",
  "lost",
];

const LEAD_STATUS_DEFAULT_LABELS = {
  new: "Новый",
  contacted: "Связались",
  in_work: "В работе",
  in_deal: "Сделка в процессе",
  won: "Выигран",
  lost: "Проигран",
};

function getLeadStatusLabel(t, status) {
  const key = String(status || "new").toLowerCase();
  return t(`lead_status_${key}`, {
    defaultValue: LEAD_STATUS_DEFAULT_LABELS[key] || key,
  });
}

function getLeadSourceLabel(t, source) {
  const value = String(source || "").trim();
  if (!value) return "--";
  return t(value, { defaultValue: value });
}

function refreshAcademyWorkflow() {
  window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
}

function ContactAvatar({ name }) {
  const initials = useMemo(() => {
    if (!name) return "??";
    return name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join("");
  }, [name]);

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center me-3"
      style={{
        width: 56,
        height: 56,
        background:
          "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.14), transparent 55%), linear-gradient(135deg, var(--accent-2), var(--accent))",
        color: "#fff",
        fontWeight: 600,
        fontSize: "1.2rem",
      }}
    >
      {initials}
    </div>
  );
}

export default function PersonalDetails() {
  const { t } = useTranslation();
  const { workflowState } = useAcademy();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const contactId = Number(id);

  const [topTab, setTopTab] = useState(() => {
    const requested = new URLSearchParams(location.search).get("academy_tab");
    return [
      "about",
      "activities",
      "works",
      "documents",
      "revenue",
      "intelligence",
    ].includes(requested)
      ? requested
      : "about";
  });
  const [timelineFilter, setTimelineFilter] = useState("all");
  const [workTimelineFilter, setWorkTimelineFilter] = useState("all");
  const [expandedWorkTimelineItems, setExpandedWorkTimelineItems] = useState(
    {},
  );
  const [selectedWorkId, setSelectedWorkId] = useState(null);
  const [selectedLeadWorkId, setSelectedLeadWorkId] = useState(null);
  const [leadEditorDraft, setLeadEditorDraft] = useState({
    status: "new",
    budget: "",
    prepayment: "",
    description: "",
  });
  const [leadEditorSaving, setLeadEditorSaving] = useState(false);
  const [leadConverting, setLeadConverting] = useState(false);
  const [leadEditorError, setLeadEditorError] = useState(null);
  const [leadEditorDirty, setLeadEditorDirty] = useState(false);
  const [showLeadWorkItems, setShowLeadWorkItems] = useState(true);
  const [showDealWorkItems, setShowDealWorkItems] = useState(true);
  const [selectedArchiveWorkId, setSelectedArchiveWorkId] = useState(null);
  const [archiveSearch, setArchiveSearch] = useState("");
  const [archiveStatusFilter, setArchiveStatusFilter] = useState("all");
  const [archivePeriodFilter, setArchivePeriodFilter] = useState("all");
  const [archiveSort, setArchiveSort] = useState("newest");
  const [archiveWithFilesOnly, setArchiveWithFilesOnly] = useState(false);
  const [dealFiles, setDealFiles] = useState([]);
  const [dealFilesLoaded, setDealFilesLoaded] = useState(false);
  const workPhotoInputRef = useRef(null);
  const [workPhotoUploadCategory, setWorkPhotoUploadCategory] =
    useState("during");
  const [workPhotoUploading, setWorkPhotoUploading] = useState(false);
  const [workPhotoError, setWorkPhotoError] = useState(null);
  const [workPhotoDragActive, setWorkPhotoDragActive] = useState(false);
  const [workPhotoLightbox, setWorkPhotoLightbox] = useState(null);
  const [workPhotoCompareOpen, setWorkPhotoCompareOpen] = useState(false);
  const [workPhotoComparePosition, setWorkPhotoComparePosition] = useState(50);
  const [filesWorkspaceLayout, setFilesWorkspaceLayout] = useState("gallery");
  const [showCompleteWorkModal, setShowCompleteWorkModal] = useState(false);
  const [completeWorkStatus, setCompleteWorkStatus] = useState("won");
  const [completeWorkSummary, setCompleteWorkSummary] = useState("");
  const [completeWorkSaving, setCompleteWorkSaving] = useState(false);
  const [completeWorkError, setCompleteWorkError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [workAiData, setWorkAiData] = useState(null);
  const [workAiLoading, setWorkAiLoading] = useState(false);
  const [workAiPanelOpen, setWorkAiPanelOpen] = useState(false);
  const [workAiError, setWorkAiError] = useState(null);
  const [workAiCopied, setWorkAiCopied] = useState(null);
  const [quickCreateModal, setQuickCreateModal] = useState(null);
  const [quickCreateTitle, setQuickCreateTitle] = useState("");
  const [quickCreateAmount, setQuickCreateAmount] = useState("");
  const [quickCreateSourceId, setQuickCreateSourceId] = useState("");
  const [quickCreateSources, setQuickCreateSources] = useState([]);
  const [quickCreateSourcesLoading, setQuickCreateSourcesLoading] =
    useState(false);
  const [quickCreateSaving, setQuickCreateSaving] = useState(false);
  const [quickCreateError, setQuickCreateError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [toast, setToast] = useState(null);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const [contact, setContact] = useState(null);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState(null);
  const [telegramEditOpen, setTelegramEditOpen] = useState(false);
  const [telegramDraft, setTelegramDraft] = useState("");
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramEditError, setTelegramEditError] = useState("");

  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);

  const [deals, setDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [dealsLoaded, setDealsLoaded] = useState(false);

  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsLoaded, setLeadsLoaded] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);

  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesLoaded, setActivitiesLoaded] = useState(false);

  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailsLoaded, setEmailsLoaded] = useState(false);

  const [showHeaderMenu, setShowHeaderMenu] = useState(false);

  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);

  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  const [activityAnalysis, setActivityAnalysis] = useState(null);
  const [activityAnalysisLoading, setActivityAnalysisLoading] = useState(false);
  const [activityAnalysisError, setActivityAnalysisError] = useState(null);

  const [aiNextStepData, setAiNextStepData] = useState(null);
  const [aiNextStepLoading, setAiNextStepLoading] = useState(false);
  const [aiNextStepError, setAiNextStepError] = useState(null);

  const [aiLeadScoreData, setAiLeadScoreData] = useState(null);
  const [aiLeadScoreLoading, setAiLeadScoreLoading] = useState(false);
  const [aiLeadScoreError, setAiLeadScoreError] = useState(null);

  const [intelTab, setIntelTab] = useState("leadScore");

  const [leadScoreLoaded, setLeadScoreLoaded] = useState(false);
  const [nextStepLoaded, setNextStepLoaded] = useState(false);
  const [activityLoaded, setActivityLoaded] = useState(false);

  const tabStyle = (key) => ({
    backgroundColor: "transparent",
    color: topTab === key ? "var(--accent)" : "var(--muted)",
    borderColor: "transparent",
    borderBottom:
      topTab === key ? "2px solid var(--accent)" : "2px solid transparent",
  });

  const editBtnStyle = {
    background: "linear-gradient(135deg, var(--accent), var(--accent-2))",
    borderColor: "transparent",
  };

  const moreBtnStyle = {
    backgroundColor: "var(--panel-2)",
    borderColor: "var(--outline)",
    color: "var(--muted)",
  };

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get("academy_tab");
    if (
      [
        "about",
        "activities",
        "works",
        "documents",
        "revenue",
        "intelligence",
      ].includes(requested)
    ) {
      setTopTab(requested);
      if (requested === "activities") {
        // The Activity can be created while this page remains mounted, so the
        // previously loaded Timeline is stale when Academy returns here.
        setTimelineFilter("activity");
        setActivitiesLoaded(false);
      }
    }
  }, [location.search]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const isTyping =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable;
      if (event.key === "Escape") {
        setQuickCreateModal(null);
        setConfirmAction(null);
        setMobileActionsOpen(false);
        if (!telegramSaving) {
          setTelegramEditOpen(false);
          setTelegramEditError("");
        }
        return;
      }
      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.toLowerCase() === "n") openQuickCreate("task");
      if (event.key.toLowerCase() === "p" && selectedWork) openPaymentModal();
      if (event.key.toLowerCase() === "f" && selectedWork)
        workPhotoInputRef.current?.click();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  useEffect(() => {
    let mounted = true;

    api
      .get("/auth/crmSettings")
      .then(({ data }) => {
        if (!mounted) return;
        const value = data?.settings_json?.files?.workspace_layout;
        setFilesWorkspaceLayout(value === "workflow" ? "workflow" : "gallery");
      })
      .catch((error) => {
        console.error("Failed to load files layout setting", error);
        if (mounted) setFilesWorkspaceLayout("gallery");
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!contactId) return;

    async function loadCompany(companyId) {
      setCompanyLoading(true);
      try {
        const resp = await api.get(`/auth/company/by-id/${companyId}`);
        setCompany(resp.data);
      } catch (err) {
        console.error("Failed to load company", err);
        setCompany(null);
      } finally {
        setCompanyLoading(false);
      }
    }

    async function loadContact() {
      setContactLoading(true);
      setContactError(null);
      try {
        const resp = await api.get(`/contacts/${contactId}`);
        const data = resp.data;
        setContact(data);

        if (data.company_id) {
          await loadCompany(data.company_id);
        } else {
          setCompany(null);
        }
      } catch (err) {
        console.error("Failed to load contact", err);
        setContactError(t("contact_error_load_contact"));
      } finally {
        setContactLoading(false);
      }
    }

    loadContact();
  }, [contactId, t]);

  useEffect(() => {
    if (!contactId) return;

    const contactUiId = Number(contact?.ui_id);

    async function loadLeads() {
      setLeadsLoading(true);
      try {
        let data = [];
        try {
          const resp = await api.get(`/leads/by-contact-id/${contactId}`);
          data = resp.data;
        } catch (directErr) {
          const resp = await api.get("/leads", {
            params: { skip: 0, limit: 500 },
          });
          const allLeads = Array.isArray(resp.data) ? resp.data : [];
          data = allLeads.filter(
            (lead) => Number(lead.contact_id) === Number(contactId),
          );
        }
        setLeads(Array.isArray(data) ? data : []);
        setLeadsLoaded(true);
      } catch (err) {
        console.error("Failed to load leads", err);
        setLeadsLoaded(true);
      } finally {
        setLeadsLoading(false);
      }
    }

    async function loadDeals() {
      setDealsLoading(true);
      try {
        const resp = await api.get(`/deals/by-contact-id/${contactId}`);
        const data = resp.data;
        setDeals(Array.isArray(data) ? data : []);
        setDealsLoaded(true);
      } catch (err) {
        console.error("Failed to load deals", err);
        setDealsLoaded(true);
      } finally {
        setDealsLoading(false);
      }
    }

    async function loadTasks() {
      setTasksLoading(true);
      try {
        const resp = await api.get(`/tasks/by-contact-id/${contactId}`);
        const data = resp.data;
        setTasks(Array.isArray(data) ? data : []);
        setTasksLoaded(true);
      } catch (err) {
        console.error("Failed to load tasks", err);
        setTasksLoaded(true);
      } finally {
        setTasksLoading(false);
      }
    }

    async function loadActivities() {
      if (!Number.isFinite(contactUiId) || contactUiId <= 0) return;

      setActivitiesLoading(true);
      try {
        const resp = await api.get(
          `/activities/timeline/contact/${contactUiId}`,
        );
        const data = resp.data;
        setActivities(Array.isArray(data) ? data : []);
        setActivitiesLoaded(true);
      } catch (err) {
        console.error("Failed to load activities", err);
        setActivitiesLoaded(true);
      } finally {
        setActivitiesLoading(false);
      }
    }

    if (
      (topTab === "about" || topTab === "revenue" || topTab === "works") &&
      !leadsLoaded &&
      !leadsLoading
    ) {
      loadLeads();
    }

    if (
      (topTab === "about" || topTab === "revenue" || topTab === "works") &&
      !dealsLoaded &&
      !dealsLoading
    ) {
      loadDeals();
    }

    if (
      (topTab === "about" || topTab === "activities" || topTab === "works") &&
      !tasksLoaded &&
      !tasksLoading
    ) {
      loadTasks();
    }

    if (
      (topTab === "about" ||
        topTab === "activities" ||
        topTab === "works" ||
        topTab === "documents") &&
      Number.isFinite(contactUiId) &&
      contactUiId > 0 &&
      !activitiesLoaded &&
      !activitiesLoading
    ) {
      loadActivities();
    }
  }, [
    contactId,
    contact?.ui_id,
    topTab,
    leadsLoaded,
    leadsLoading,
    dealsLoaded,
    dealsLoading,
    tasksLoaded,
    tasksLoading,
    activitiesLoaded,
    activitiesLoading,
  ]);

  useEffect(() => {
    if (!contact || topTab !== "activities" || emailsLoaded || emailsLoading) {
      return;
    }

    async function loadInbox() {
      setEmailsLoading(true);
      try {
        const resp = await api.get("/email/inbox", {
          params: { limit: 20 },
        });
        const data = Array.isArray(resp.data) ? resp.data : [];

        const primaryEmail = getPrimaryEmail(contact);
        if (primaryEmail) {
          setEmails(
            data.filter(
              (m) =>
                m.to_email === primaryEmail || m.from_email === primaryEmail,
            ),
          );
        } else {
          setEmails(data);
        }
        setEmailsLoaded(true);
      } catch (err) {
        console.error("Failed to load inbox", err);
        setEmailsLoaded(true);
      } finally {
        setEmailsLoading(false);
      }
    }

    loadInbox();
  }, [contact, topTab, emailsLoaded, emailsLoading]);

  useEffect(() => {
    if (topTab !== "works" || dealFilesLoaded) return;

    async function loadDealFiles() {
      try {
        const resp = await api.get("/auth/storage/files", {
          params: { entity_type: "deal" },
        });
        setDealFiles(Array.isArray(resp.data) ? resp.data : []);
      } catch (err) {
        console.error("Failed to load work files", err);
      } finally {
        setDealFilesLoaded(true);
      }
    }

    loadDealFiles();
  }, [topTab, dealFilesLoaded]);

  useEffect(() => {
    if (topTab !== "intelligence") return;

    if (intelTab === "leadScore" && !leadScoreLoaded) {
      handleGenerateLeadScore();
    }

    if (intelTab === "nextStep" && !nextStepLoaded) {
      handleGenerateNextStep();
    }

    if (intelTab === "activity" && !activityLoaded) {
      handleAnalyzeActivity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    topTab,
    intelTab,
    leadScoreLoaded,
    nextStepLoaded,
    activityLoaded,
    contactId,
  ]);

  const openCompleteWork = () => {
    if (!selectedWork) return;
    setCompleteWorkStatus("won");
    setCompleteWorkSummary(selectedWork.work_summary || "");
    setCompleteWorkError(null);
    setShowCompleteWorkModal(true);
  };

  const closeCompleteWork = () => {
    if (completeWorkSaving) return;
    setShowCompleteWorkModal(false);
    setCompleteWorkError(null);
  };

  const handleCompleteWork = async () => {
    const summary = completeWorkSummary.trim();
    if (summary.length < 3 || !selectedWork?.id) {
      setCompleteWorkError(
        t("work_summary_required", {
          defaultValue: "Кратко опишите выполненную работу.",
        }),
      );
      return;
    }

    setCompleteWorkSaving(true);
    setCompleteWorkError(null);
    try {
      const resp = await api.put(`/deals/${selectedWork.id}`, {
        status: completeWorkStatus,
        work_summary: summary,
      });
      setDeals((prev) =>
        prev.map((deal) => (deal.id === selectedWork.id ? resp.data : deal)),
      );
      setSelectedArchiveWorkId(selectedWork.id);
      setSelectedWorkId(null);
      setShowCompleteWorkModal(false);
    } catch (err) {
      console.error("Failed to complete work", err);
      setCompleteWorkError(
        err?.response?.data?.detail ||
          t("work_complete_error", {
            defaultValue: "Не удалось завершить работу.",
          }),
      );
    } finally {
      setCompleteWorkSaving(false);
    }
  };

  const openPaymentModal = () => {
    if (!selectedWork?.id) return;
    setPaymentAmount("");
    setPaymentMethod("cash");
    setPaymentNote("");
    setPaymentError(null);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    if (paymentSaving) return;
    setShowPaymentModal(false);
    setPaymentError(null);
  };

  const handleAddPayment = async () => {
    if (!selectedWork?.id) return;
    const amount = Number(String(paymentAmount).replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError(
        t("payment_amount_invalid", {
          defaultValue: "Введите корректную сумму оплаты.",
        }),
      );
      return;
    }

    const currentPaid = Number(selectedWork.prepayment || 0);
    const total = Number(selectedWork.amount || 0);
    const nextPaid = currentPaid + amount;
    if (total > 0 && nextPaid > total + 0.001) {
      setPaymentError(
        t("payment_exceeds_balance", {
          defaultValue: "Сумма оплаты превышает остаток по работе.",
        }),
      );
      return;
    }

    setPaymentSaving(true);
    setPaymentError(null);
    try {
      const dealResp = await api.put(`/deals/${selectedWork.id}`, {
        prepayment: nextPaid,
        balance: Math.max(total - nextPaid, 0),
      });

      setDeals((prev) =>
        prev.map((deal) =>
          deal.id === selectedWork.id ? dealResp.data : deal,
        ),
      );

      try {
        const activityResp = await api.post("/activities", {
          type: "note",
          subject: t("payment_received", { defaultValue: "Получена оплата" }),
          content: [
            `Payment amount: ${amount.toFixed(2)} EUR`,
            `Payment method: ${paymentMethod}`,
            paymentNote.trim() ? `Payment note: ${paymentNote.trim()}` : "",
          ]
            .filter(Boolean)
            .join("\n"),
          contact_id: contactId,
          deal_id: selectedWork.id,
          lead_id: selectedWork.lead_id || null,
          when_at: new Date().toISOString(),
        });
        setActivities((prev) => [activityResp.data, ...prev]);
        setActivitiesLoaded(true);
      } catch (activityErr) {
        console.warn(
          "Payment saved, but payment activity was not created",
          activityErr,
        );
      }

      setShowPaymentModal(false);
    } catch (err) {
      console.error("Failed to add payment", err);
      setPaymentError(
        err?.response?.data?.detail ||
          t("payment_save_error", {
            defaultValue: "Не удалось сохранить оплату.",
          }),
      );
    } finally {
      setPaymentSaving(false);
    }
  };

  const generateWorkAi = async () => {
    if (!selectedWork?.id || workAiLoading) return;
    setWorkAiLoading(true);
    setWorkAiError(null);
    setWorkAiCopied(null);
    try {
      const data = await aiApi.generateDealWorkAssistant(selectedWork.id);
      setWorkAiData(data);
    } catch (err) {
      console.error("Failed to generate work AI assistant", err);
      setWorkAiError(
        err?.response?.data?.detail ||
          t("work_ai_error", {
            defaultValue: "Не удалось проанализировать выбранную работу.",
          }),
      );
    } finally {
      setWorkAiLoading(false);
    }
  };

  const copyWorkAiText = async (key, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setWorkAiCopied(key);
      window.setTimeout(() => setWorkAiCopied(null), 1800);
    } catch (err) {
      console.error("Failed to copy AI text", err);
    }
  };

  const useAiCompletionSummary = () => {
    if (!workAiData?.completion_summary || !selectedWork) return;
    setCompleteWorkStatus("won");
    setCompleteWorkSummary(workAiData.completion_summary);
    setCompleteWorkError(null);
    setShowCompleteWorkModal(true);
  };

  async function openCompanyModal() {
    setShowCompanyModal(true);
    setAssignError(null);
    setCompaniesError(null);

    setSelectedCompanyId(company?.id ? String(company.id) : "");

    if (companies.length > 0) return;

    setCompaniesLoading(true);
    try {
      const resp = await api.get("/auth/company/all", {
        params: { skip: 0, limit: 200 },
      });
      const items = Array.isArray(resp.data) ? resp.data : [];
      setCompanies(items);
    } catch (e) {
      console.error("Failed to load companies", e);
      setCompaniesError(t("error_loading_companies"));
    } finally {
      setCompaniesLoading(false);
    }
  }

  function closeCompanyModal() {
    setShowCompanyModal(false);
    setAssignError(null);
  }

  async function handleAssignCompany() {
    setAssignError(null);

    const cid = Number(selectedCompanyId);
    if (!cid) {
      setAssignError(t("contact_assign_error_select_company"));
      return;
    }

    setAssignLoading(true);
    try {
      await api.put(`/contacts/${contactId}/company`, { company_id: cid });

      setContact((prev) => (prev ? { ...prev, company_id: cid } : prev));

      setCompanyLoading(true);
      try {
        const resp = await api.get(`/auth/company/by-id/${cid}`);
        setCompany(resp.data);
      } catch (e) {
        console.error("Failed to refresh company", e);
      } finally {
        setCompanyLoading(false);
      }

      setShowCompanyModal(false);
    } catch (e) {
      console.error("Failed to assign company", e);
      setAssignError(t("contact_assign_error_failed"));
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleUnassignCompany() {
    setAssignError(null);
    setAssignLoading(true);
    try {
      await api.put(`/contacts/${contactId}/company`, { company_id: null });
      setContact((prev) => (prev ? { ...prev, company_id: null } : prev));
      setCompany(null);
      setShowCompanyModal(false);
    } catch (e) {
      console.error("Failed to unassign company", e);
      setAssignError(t("contact_unassign_error_failed"));
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleGenerateSummary() {
    if (!contactId) return;

    setAiLoading(true);
    setAiError(null);

    try {
      const data = await aiApi.generateContactSummary(contactId);
      setAiSummary(data);
    } catch (e) {
      console.error("Failed to generate AI summary", e);
      setAiError(e?.response?.data?.detail || t("error_generate_ai_draft"));
    } finally {
      setAiLoading(false);
    }
  }

  async function handleGenerateNextStep(force = false) {
    if (!contactId) return;
    if (aiNextStepLoading) return;
    if (!force && nextStepLoaded) return;

    setAiNextStepLoading(true);
    setAiNextStepError(null);

    try {
      const data = await aiApi.generateNextStep(contactId);
      setAiNextStepData(data);
      setNextStepLoaded(true);
    } catch (e) {
      console.error("Failed to generate next step", e);
      setAiNextStepError(
        e?.response?.data?.detail || t("contact_next_step_error"),
      );
    } finally {
      setAiNextStepLoading(false);
    }
  }

  async function handleGenerateLeadScore(force = false) {
    if (!contactId) return;
    if (aiLeadScoreLoading) return;
    if (!force && leadScoreLoaded) return;

    setAiLeadScoreLoading(true);
    setAiLeadScoreError(null);

    try {
      const data = await aiApi.generateLeadScore(contactId);
      setAiLeadScoreData(data);
      setLeadScoreLoaded(true);
    } catch (e) {
      console.error("Failed to generate lead score", e);
      setAiLeadScoreError(
        e?.response?.data?.detail || t("contact_lead_score_error"),
      );
    } finally {
      setAiLeadScoreLoading(false);
    }
  }

  async function handleCopySummary() {
    if (!aiSummary) return;

    const text = [
      aiSummary.summary || "",
      "",
      ...(aiSummary.highlights || []).map((x) => `• ${x}`),
      "",
      aiSummary.next_step
        ? `${t("contact_next_step")}: ${aiSummary.next_step}`
        : "",
      aiSummary.risk_level
        ? `${t("contact_risk_level")}: ${aiSummary.risk_level}`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.error("Failed to copy summary", e);
    }
  }

  async function handleAnalyzeActivity(force = false) {
    if (!contactId) return;
    if (activityAnalysisLoading) return;
    if (!force && activityLoaded) return;

    setActivityAnalysisLoading(true);
    setActivityAnalysisError(null);

    try {
      const data = await aiApi.analyzeContactActivity(contactId);
      setActivityAnalysis(data);
      setActivityLoaded(true);
    } catch (e) {
      console.error("Failed to analyze activity", e);
      setActivityAnalysisError(
        e?.response?.data?.detail || t("contact_activity_analysis_error"),
      );
    } finally {
      setActivityAnalysisLoading(false);
    }
  }

  const openContactLead = (lead) => {
    if (lead?.id) navigate(`/menu/sales/leads?lead_id=${lead.id}`);
  };

  const openContactDeal = (deal) => {
    if (deal?.id) navigate(`/menu/sales/deals?deal_id=${deal.id}`);
  };

  const openContactTask = (task) => {
    if (task?.id) navigate(`/menu/sales/tasks?task_id=${task.id}`);
  };

  const openAllContactTasks = () =>
    navigate(`/menu/sales/tasks?contact_id=${contactId}`);
  const openAllContactLeads = () =>
    navigate(`/menu/sales/leads?contact_id=${contactId}`);
  const openAllContactDeals = () =>
    navigate(`/menu/sales/deals?contact_id=${contactId}`);
  const openAllContactActivities = () =>
    navigate(`/menu/sales/activities?contact_id=${contactId}`);

  const openEditContact = () => {
    setShowHeaderMenu(false);
    navigate(`/menu/sales/contacts?contact_id=${contactId}&edit=1`);
  };

  const openTelegramEditor = () => {
    setTelegramDraft(normalizeTelegramUsername(contact?.telegram_username));
    setTelegramEditError("");
    setTelegramEditOpen(true);
  };

  const closeTelegramEditor = () => {
    if (telegramSaving) return;
    setTelegramEditOpen(false);
    setTelegramEditError("");
  };

  const saveTelegramUsername = async () => {
    if (!contactId || telegramSaving) return;

    const username = normalizeTelegramUsername(telegramDraft);
    if (username && !/^[A-Za-z0-9_]{5,32}$/.test(username)) {
      setTelegramEditError(
        t("telegram_username_invalid", {
          defaultValue: "Используйте 5–32 латинских символа, цифры или _.",
        }),
      );
      return;
    }

    try {
      setTelegramSaving(true);
      setTelegramEditError("");
      await api.put(`/contacts/${contactId}`, {
        telegram_username: username || null,
      });
      setContact((prev) =>
        prev ? { ...prev, telegram_username: username || null } : prev,
      );
      setTelegramEditOpen(false);
      showToast(t("contact_updated", { defaultValue: "Telegram обновлён." }));
    } catch (error) {
      console.error("Failed to update Telegram username", error);
      setTelegramEditError(
        getApiErrorText(
          error,
          t("error_updating_contact", {
            defaultValue: "Не удалось обновить Telegram.",
          }),
        ),
      );
    } finally {
      setTelegramSaving(false);
    }
  };

  const openCompanyDetails = () => {
    if (!company?.id) return;
    navigate(`/menu/companies?company_id=${company.id}`);
  };

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    window.clearTimeout(showToast._timer);
    showToast._timer = window.setTimeout(() => setToast(null), 3200);
  };

  const loadQuickCreateSources = async () => {
    if (quickCreateSources.length > 0 || quickCreateSourcesLoading) return;

    setQuickCreateSourcesLoading(true);
    try {
      const response = await api.get("/sources", {
        params: { skip: 0, limit: 1000 },
      });
      setQuickCreateSources(
        Array.isArray(response.data) ? response.data : [],
      );
    } catch (error) {
      setQuickCreateError(
        error?.response?.data?.detail ||
          t("error_loading_sources", {
            defaultValue: "Не удалось загрузить источники.",
          }),
      );
    } finally {
      setQuickCreateSourcesLoading(false);
    }
  };

  const openQuickCreate = (type) => {
    setShowHeaderMenu(false);
    setMobileActionsOpen(false);
    setQuickCreateError(null);
    setQuickCreateAmount("");
    setQuickCreateSourceId("");
    const defaults = {
      lead: contact?.full_name ? `Lead - ${contact.full_name}` : "New lead",
      task: contact?.full_name ? `Follow up ${contact.full_name}` : "Follow up",
      deal: contact?.full_name ? `Deal - ${contact.full_name}` : "New deal",
    };
    setQuickCreateTitle(defaults[type] || "");
    setQuickCreateModal(type);
    if (type === "lead") {
      loadQuickCreateSources();
    }
  };

  const closeQuickCreate = () => {
    if (quickCreateSaving) return;
    setQuickCreateModal(null);
    setQuickCreateError(null);
  };

  const handleQuickCreate = async () => {
    const title = quickCreateTitle.trim();
    if (!title) {
      setQuickCreateError(
        t("required_field", { defaultValue: "Укажите название." }),
      );
      return;
    }

    setQuickCreateSaving(true);
    setQuickCreateError(null);
    try {
      if (quickCreateModal === "lead") {
        const sourceId = Number(quickCreateSourceId);
        if (!Number.isInteger(sourceId) || sourceId <= 0) {
          setQuickCreateError(
            t("lead_source_required", {
              defaultValue: "Выберите источник лида.",
            }),
          );
          return;
        }

        const resp = await api.post("/leads", {
          title,
          contact_id: contactId,
          source_id: sourceId,
        });
        setLeads((prev) => [resp.data, ...prev]);
        setLeadsLoaded(true);
        setQuickCreateModal(null);
        showToast(t("lead_created", { defaultValue: "Лид создан" }));
        refreshAcademyWorkflow();
        openContactLead(resp.data);
        return;
      }

      if (quickCreateModal === "task") {
        const resp = await api.post("/tasks", { title, contact_id: contactId });
        setTasks((prev) => [resp.data, ...prev]);
        setTasksLoaded(true);
        setQuickCreateModal(null);
        showToast(t("task_created", { defaultValue: "Задача создана" }));
        return;
      }

      if (quickCreateModal === "deal") {
        const stagesResp = await api.get("/pipeline-stages");
        const stages = Array.isArray(stagesResp.data) ? stagesResp.data : [];
        const stage = stages[0];
        if (!stage?.id)
          throw new Error(
            t("contact_no_pipeline_stage", {
              defaultValue: "Не найден этап воронки.",
            }),
          );
        const amountValue = String(quickCreateAmount || "").trim();
        const amount = amountValue
          ? Number(amountValue.replace(",", "."))
          : null;
        if (amountValue && (!Number.isFinite(amount) || amount < 0)) {
          throw new Error(
            t("invalid_amount", { defaultValue: "Введите корректную сумму." }),
          );
        }
        const resp = await api.post("/deals", {
          title,
          amount,
          contact_id: contactId,
          stage_id: stage.id,
        });
        setDeals((prev) => [resp.data, ...prev]);
        setDealsLoaded(true);
        setQuickCreateModal(null);
        showToast(t("deal_created", { defaultValue: "Сделка создана" }));
        openContactDeal(resp.data);
      }
    } catch (err) {
      console.error("Failed to create entity", err);
      setQuickCreateError(
        err?.response?.data?.detail ||
          err?.message ||
          t("save_failed", { defaultValue: "Не удалось сохранить." }),
      );
    } finally {
      setQuickCreateSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    setShowHeaderMenu(false);

    try {
      const { data } = await api.get(`/contacts/${contactId}/delete-preview`);

      const lines = [
        t("confirm_delete_contact_name", {
          name: contact?.full_name || "",
          defaultValue:
            "Контакт {{name}} будет удалён вместе со всей связанной CRM-цепочкой.",
        }),
        "",
        t("will_be_deleted", { defaultValue: "Будет удалено:" }),
        `• ${t("sales_leads", { defaultValue: "Лиды" })}: ${Number(data?.leads || 0)}`,
        `• ${t("sales_tasks", { defaultValue: "Задачи" })}: ${Number(data?.tasks || 0)}`,
        `• ${t("sales_deals", { defaultValue: "Сделки" })}: ${Number(data?.deals || 0)}`,
        `• ${t("sales_activities", { defaultValue: "Активности" })}: ${Number(data?.activities || 0)}`,
        `• ${t("files", { defaultValue: "Файлы" })}: ${Number(data?.files || 0)}`,
        "",
        t("delete_cannot_be_undone", {
          defaultValue: "Это действие нельзя отменить.",
        }),
      ];

      setConfirmAction({
        type: "delete-contact",
        title: t("delete_contact", { defaultValue: "Удалить контакт?" }),
        message: lines.join("\n"),
        danger: true,
      });
    } catch (err) {
      console.error("Failed to load contact delete preview", err);
      setConfirmAction({
        type: "delete-contact",
        title: t("delete_contact", { defaultValue: "Удалить контакт?" }),
        message: t("confirm_delete_contact", {
          name: contact?.full_name || "",
          defaultValue:
            "Контакт {{name}} и вся его CRM-цепочка будут удалены. Это действие нельзя отменить.",
        }),
        danger: true,
      });
    }
  };

  const confirmDeleteContact = async () => {
    try {
      await api.delete(`/contacts/${contactId}`);
      navigate("/menu/sales/contacts");
    } catch (err) {
      console.error("Failed to delete contact", err);
      setConfirmAction(null);
      showToast(
        err?.response?.data?.detail ||
          t("error_deleting_contact", {
            defaultValue: "Не удалось удалить контакт",
          }),
        "danger",
      );
    }
  };

  const createLeadForContact = () => openQuickCreate("lead");
  const createTaskForContact = () => openQuickCreate("task");
  const createDealForContact = () => openQuickCreate("deal");

  const showComingSoon = (label) => {
    setShowHeaderMenu(false);
    showToast(
      t("feature_coming_soon", {
        label,
        defaultValue:
          "{{label}} будет доступно в одном из следующих обновлений.",
      }),
      "info",
    );
  };

  const handleCompleteContactTask = async (task) => {
    if (!task?.id || task.status === "done") return;
    try {
      await api.put(`/tasks/${task.id}`, { status: "done" });
      setTasks((prev) =>
        prev.map((item) =>
          item.id === task.id ? { ...item, status: "done" } : item,
        ),
      );
    } catch (err) {
      console.error("Failed to mark task as done", err);
    }
  };

  const primaryEmail = getPrimaryEmail(contact);
  const primaryPhone = getPrimaryPhone(contact);

  const lifecycleStage = contact?.status || "cold";
  const ownerName = contact?.owner_full_name || t("contact_no_owner");
  const lastContacted = contact?.last_contacted || null;

  const leadsCount = leads.length;
  const openLeads = leads.filter(
    (lead) =>
      !["won", "lost"].includes(String(lead.status || "").toLowerCase()),
  );
  const tasksCount = tasks.length;
  const openTasks = tasks.filter(
    (task) => String(task.status || "").toLowerCase() !== "done",
  );
  const activitiesCount = activities.length;
  const latestLead = [...leads].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
  )[0];
  const latestTask = [...tasks].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
  )[0];
  const nextOpenTask = [...openTasks].sort((a, b) => {
    const av = a.due_at
      ? new Date(a.due_at).getTime()
      : Number.MAX_SAFE_INTEGER;
    const bv = b.due_at
      ? new Date(b.due_at).getTime()
      : Number.MAX_SAFE_INTEGER;
    return av - bv;
  })[0];
  const contactAttachments = Array.isArray(contact?.attachments)
    ? contact.attachments
    : [];

  const dealsCount = deals.length;
  const openDeals = deals.filter((d) => d.status === "open");
  const wonDeals = deals.filter((d) => d.status === "won");
  const totalAmount = deals.reduce(
    (sum, d) => sum + (Number(d.amount) || 0),
    0,
  );
  const paidAmount = deals.reduce(
    (sum, d) => sum + (Number(d.prepayment) || 0),
    0,
  );
  const balanceDue = deals.reduce((sum, d) => {
    const explicitBalance = Number(d.balance);
    if (Number.isFinite(explicitBalance)) return sum + explicitBalance;
    return (
      sum + Math.max((Number(d.amount) || 0) - (Number(d.prepayment) || 0), 0)
    );
  }, 0);
  const latestActivity = [...activities].sort(
    (a, b) =>
      new Date(b.when_at || b.completed_at || b.created_at || 0) -
      new Date(a.when_at || a.completed_at || a.created_at || 0),
  )[0];
  const latestFinishedDeal = [...deals]
    .filter((deal) =>
      ["won", "lost"].includes(String(deal.status || "").toLowerCase()),
    )
    .sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
    )[0];
  const telegramUsername = normalizeTelegramUsername(
    contact?.telegram_username,
  );
  const telegramUsernameLabel = formatTelegramUsername(
    contact?.telegram_username,
  );
  const telegramLabel =
    (telegramUsername ? `@${telegramUsername}` : null) ||
    contact?.telegram_first_name ||
    (contact?.telegram_chat_id ? `ID ${contact.telegram_chat_id}` : null);

  const activeWorks = deals.filter(
    (deal) => String(deal.status || "").toLowerCase() === "open",
  );

  const convertedLeadIds = new Set(
    deals
      .map((deal) => Number(deal.lead_id))
      .filter((leadId) => Number.isFinite(leadId) && leadId > 0),
  );

  const activeLeadWorks = leads
    .filter((lead) => !convertedLeadIds.has(Number(lead.id)))
    .filter(
      (lead) =>
        !["won", "lost", "in_deal"].includes(
          String(lead.status || "").toLowerCase(),
        ),
    )
    .sort(
      (a, b) =>
        new Date(b.updated_at || b.created_at || 0) -
        new Date(a.updated_at || a.created_at || 0),
    );

  useEffect(() => {
    const academyParams = new URLSearchParams(location.search);
    const requestedDealId = academyParams.get("academy_deal_id");
    const requestedLeadId = academyParams.get("academy_lead_id");
    if (
      requestedDealId &&
      deals.some((deal) => Number(deal.id) === Number(requestedDealId))
    ) {
      setSelectedWorkId(Number(requestedDealId));
      setSelectedLeadWorkId(null);
      return;
    }
    if (
      requestedLeadId &&
      leads.some((lead) => Number(lead.id) === Number(requestedLeadId))
    ) {
      setSelectedLeadWorkId(Number(requestedLeadId));
      setSelectedWorkId(null);
    }
  }, [deals, leads, location.search]);

  const visibleLeadWorks = showLeadWorkItems ? activeLeadWorks : [];
  const visibleDealWorks = showDealWorkItems ? activeWorks : [];
  const activeWorkItemsCount =
    visibleLeadWorks.length + visibleDealWorks.length;

  const archivedWorks = deals.filter((deal) =>
    ["won", "lost"].includes(String(deal.status || "").toLowerCase()),
  );

  const archiveCutoff = (() => {
    const now = new Date();
    if (archivePeriodFilter === "30d")
      return new Date(now.getTime() - 30 * 86400000);
    if (archivePeriodFilter === "90d")
      return new Date(now.getTime() - 90 * 86400000);
    if (archivePeriodFilter === "year")
      return new Date(now.getFullYear(), 0, 1);
    return null;
  })();

  const archiveWonCount = archivedWorks.filter(
    (deal) => String(deal.status || "").toLowerCase() === "won",
  ).length;
  const archiveLostCount = archivedWorks.length - archiveWonCount;
  const archiveRevenue = archivedWorks
    .filter((deal) => String(deal.status || "").toLowerCase() === "won")
    .reduce((sum, deal) => sum + Number(deal.amount || 0), 0);

  const selectedArchiveWork =
    archivedWorks.find(
      (deal) => Number(deal.id) === Number(selectedArchiveWorkId),
    ) || null;

  const getWorkTasks = (deal) =>
    tasks.filter(
      (task) =>
        Number(task.deal_id) === Number(deal.id) ||
        (deal.lead_id && Number(task.lead_id) === Number(deal.lead_id)),
    );

  const getWorkActivities = (deal) =>
    activities.filter(
      (activity) =>
        Number(activity.deal_id) === Number(deal.id) ||
        (deal.lead_id && Number(activity.lead_id) === Number(deal.lead_id)),
    );

  const getWorkFiles = (deal) => {
    const activityAttachments = getWorkActivities(deal).flatMap((activity) =>
      Array.isArray(activity.file_attachments) ? activity.file_attachments : [],
    );
    const directAttachments = dealFiles.filter(
      (file) => Number(file.entity_id) === Number(deal.id),
    );
    const unique = new Map();
    [...activityAttachments, ...directAttachments].forEach((file) => {
      unique.set(file.id || `${file.provider_file_id}-${file.file_name}`, file);
    });
    return [...unique.values()];
  };

  const filteredArchivedWorks = archivedWorks
    .filter((deal) => {
      const haystack =
        `${deal.title || ""} ${deal.work_summary || ""} ${deal.ui_id || ""}`.toLowerCase();
      const matchesSearch =
        !archiveSearch.trim() ||
        haystack.includes(archiveSearch.trim().toLowerCase());
      const matchesStatus =
        archiveStatusFilter === "all" ||
        String(deal.status || "").toLowerCase() === archiveStatusFilter;
      const completedDate = new Date(
        deal.completed_at || deal.updated_at || deal.created_at || 0,
      );
      const matchesPeriod =
        !archiveCutoff ||
        (!Number.isNaN(completedDate.getTime()) &&
          completedDate >= archiveCutoff);
      const matchesFiles =
        !archiveWithFilesOnly || getWorkFiles(deal).length > 0;
      return matchesSearch && matchesStatus && matchesPeriod && matchesFiles;
    })
    .sort((a, b) => {
      if (archiveSort === "oldest")
        return (
          new Date(a.completed_at || a.created_at || 0) -
          new Date(b.completed_at || b.created_at || 0)
        );
      if (archiveSort === "amount_desc")
        return Number(b.amount || 0) - Number(a.amount || 0);
      if (archiveSort === "amount_asc")
        return Number(a.amount || 0) - Number(b.amount || 0);
      return (
        new Date(b.completed_at || b.created_at || 0) -
        new Date(a.completed_at || a.created_at || 0)
      );
    });

  const selectedWork =
    activeWorks.find((deal) => Number(deal.id) === Number(selectedWorkId)) ||
    (!selectedLeadWorkId ? activeWorks[0] || null : null);

  const selectedLeadWork =
    activeLeadWorks.find(
      (lead) => Number(lead.id) === Number(selectedLeadWorkId),
    ) || null;

  const getLeadWorkTasks = (lead) =>
    tasks.filter((task) => Number(task.lead_id) === Number(lead.id));

  const getLeadWorkActivities = (lead) =>
    activities.filter(
      (activity) => Number(activity.lead_id) === Number(lead.id),
    );

  const selectedLeadTasks = selectedLeadWork
    ? getLeadWorkTasks(selectedLeadWork)
    : [];

  const selectedLeadActivities = selectedLeadWork
    ? getLeadWorkActivities(selectedLeadWork)
    : [];

  const selectedLeadCompletedTasks = selectedLeadTasks.filter(
    (task) => String(task.status || "").toLowerCase() === "done",
  ).length;

  const selectedLeadProgress =
    selectedLeadTasks.length > 0
      ? Math.round(
          (selectedLeadCompletedTasks / selectedLeadTasks.length) * 100,
        )
      : 0;

  const selectedLeadNextTask = [...selectedLeadTasks]
    .filter((task) => String(task.status || "").toLowerCase() !== "done")
    .sort((a, b) => {
      const av = a.due_at
        ? new Date(a.due_at).getTime()
        : Number.MAX_SAFE_INTEGER;
      const bv = b.due_at
        ? new Date(b.due_at).getTime()
        : Number.MAX_SAFE_INTEGER;
      return av - bv;
    })[0];

  const selectedLeadPrimaryTask = selectedLeadTasks[0] || null;

  const selectedLeadHasBudget =
    selectedLeadWork?.budget !== null &&
    selectedLeadWork?.budget !== undefined &&
    selectedLeadWork?.budget !== "" &&
    Number(selectedLeadWork?.budget) > 0;

  const selectedLeadHasTask = selectedLeadTasks.length > 0;

  const academyWorkflowSample = workflowState?.sample;
  const selectedLeadIsAcademyLead = Boolean(
    workflowState?.key === "crm_full_cycle" &&
      selectedLeadWork &&
      academyWorkflowSample &&
      (Number(academyWorkflowSample.leadId) === Number(selectedLeadWork.id) ||
        (academyWorkflowSample.marker &&
          String(selectedLeadWork.title || "").includes(
            academyWorkflowSample.marker,
          ))),
  );
  const selectedLeadFinanceLockedByAcademy =
    selectedLeadIsAcademyLead && !selectedLeadHasTask;

  const selectedLeadReadyForDeal = selectedLeadHasBudget && selectedLeadHasTask;

  const selectedLeadDraftBudget =
    leadEditorDraft.budget === ""
      ? null
      : Number(String(leadEditorDraft.budget).replace(",", "."));

  const selectedLeadDraftPrepayment =
    leadEditorDraft.prepayment === ""
      ? 0
      : Number(String(leadEditorDraft.prepayment).replace(",", "."));

  const selectedLeadDraftBalance =
    selectedLeadDraftBudget === null ||
    !Number.isFinite(selectedLeadDraftBudget)
      ? null
      : Math.max(
          Number(
            (
              selectedLeadDraftBudget -
              (Number.isFinite(selectedLeadDraftPrepayment)
                ? selectedLeadDraftPrepayment
                : 0)
            ).toFixed(2),
          ),
          0,
        );

  useEffect(() => {
    if (!selectedLeadWork) {
      setLeadEditorDraft({
        status: "new",
        budget: "",
        prepayment: "",
        description: "",
      });
      setLeadEditorDirty(false);
      setLeadEditorError(null);
      return;
    }

    setLeadEditorDraft({
      status: selectedLeadWork.status || "new",
      budget:
        selectedLeadWork.budget === null ||
        selectedLeadWork.budget === undefined
          ? ""
          : String(selectedLeadWork.budget),
      prepayment:
        selectedLeadWork.prepayment === null ||
        selectedLeadWork.prepayment === undefined
          ? ""
          : String(selectedLeadWork.prepayment),
      description: selectedLeadWork.description || "",
    });
    setLeadEditorDirty(false);
    setLeadEditorError(null);
  }, [selectedLeadWork?.id]);

  const updateLeadEditorDraft = (field, value) => {
    setLeadEditorDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
    setLeadEditorDirty(true);
    setLeadEditorError(null);
  };

  const saveSelectedLeadWorkspace = async () => {
    if (!selectedLeadWork?.id || leadEditorSaving) return;

    const budget =
      leadEditorDraft.budget === ""
        ? null
        : Number(String(leadEditorDraft.budget).replace(",", "."));
    const prepayment =
      leadEditorDraft.prepayment === ""
        ? null
        : Number(String(leadEditorDraft.prepayment).replace(",", "."));

    if (budget !== null && (!Number.isFinite(budget) || budget < 0)) {
      setLeadEditorError(
        t("invalid_lead_budget", {
          defaultValue: "Бюджет должен быть положительным числом.",
        }),
      );
      return;
    }

    if (
      prepayment !== null &&
      (!Number.isFinite(prepayment) || prepayment < 0)
    ) {
      setLeadEditorError(
        t("invalid_lead_prepayment", {
          defaultValue: "Предоплата должна быть положительным числом.",
        }),
      );
      return;
    }

    if (budget !== null && prepayment !== null && prepayment > budget) {
      setLeadEditorError(
        t("prepayment_exceeds_budget", {
          defaultValue: "Предоплата не может быть больше бюджета.",
        }),
      );
      return;
    }

    setLeadEditorSaving(true);
    setLeadEditorError(null);

    try {
      const response = await api.put(`/leads/${selectedLeadWork.id}`, {
        status: leadEditorDraft.status,
        budget,
        prepayment,
        description: leadEditorDraft.description.trim() || null,
      });

      const updatedLead = response.data || {
        ...selectedLeadWork,
        status: leadEditorDraft.status,
        budget,
        prepayment,
        balance:
          budget === null
            ? null
            : Math.max(
                Number((budget - Number(prepayment || 0)).toFixed(2)),
                0,
              ),
        description: leadEditorDraft.description.trim() || null,
      };

      setLeads((prev) =>
        prev.map((lead) =>
          Number(lead.id) === Number(selectedLeadWork.id)
            ? { ...lead, ...updatedLead }
            : lead,
        ),
      );

      setLeadEditorDirty(false);
      refreshAcademyWorkflow();
      showToast(
        t("lead_saved", {
          defaultValue: "Изменения лида сохранены.",
        }),
      );
    } catch (error) {
      console.error("Failed to update lead workspace", error);
      setLeadEditorError(
        error?.response?.data?.detail ||
          t("error_updating_lead", {
            defaultValue: "Не удалось сохранить изменения лида.",
          }),
      );
    } finally {
      setLeadEditorSaving(false);
    }
  };

  const createTaskForSelectedLead = () => {
    if (!selectedLeadWork) return;

    const academySample = academyWorkflowSample;
    const isAcademyLead = selectedLeadIsAcademyLead;

    navigate("/menu/sales/tasks", {
      state: {
        lead_id: selectedLeadWork.id,
        lead_ui_id: selectedLeadWork.ui_id,
        contact_ids: [contactId],
        contact_ui_ids: contact?.ui_id ? [contact.ui_id] : [],
        default_title: isAcademyLead
          ? academySample.taskTitle
          : selectedLeadWork.title,
        default_description: isAcademyLead ? academySample.taskDescription : "",
        default_due_at: isAcademyLead ? academySample.taskDueAt : "",
        academy_return_to: `/menu/sales/contacts/${contactId}/personal-details?academy_tab=works&academy_lead_id=${selectedLeadWork.id}`,
      },
    });
  };

  const openSelectedLeadTask = () => {
    if (!selectedLeadPrimaryTask) return;
    openContactTask(selectedLeadPrimaryTask);
  };

  const openSelectedLeadForConversion = async () => {
    if (!selectedLeadWork?.id || !selectedLeadReadyForDeal || leadConverting) {
      return;
    }

    if (leadEditorDirty) {
      setLeadEditorError(
        t("save_lead_before_convert", {
          defaultValue:
            "Сначала сохраните изменения лида, затем выполните конвертацию.",
        }),
      );
      return;
    }

    const confirmed = window.confirm(
      t("confirm_convert_lead_to_deal", {
        defaultValue:
          "Вы уверены, что хотите конвертировать этот лид в сделку?",
      }),
    );

    if (!confirmed) return;

    const convertedLeadId = Number(selectedLeadWork.id);

    setLeadConverting(true);
    setLeadEditorError(null);

    try {
      const response = await api.post(
        `/leads/${convertedLeadId}/convert-to-deal`,
      );

      const responseDeal =
        response?.data?.deal || response?.data?.item || response?.data || null;

      const [leadsResponse, dealsResponse] = await Promise.all([
        api.get(`/leads/by-contact-id/${contactId}`).catch(async () => {
          const fallback = await api.get("/leads", {
            params: { skip: 0, limit: 500 },
          });
          const allLeads = Array.isArray(fallback.data) ? fallback.data : [];
          return {
            data: allLeads.filter(
              (lead) => Number(lead.contact_id) === Number(contactId),
            ),
          };
        }),
        api.get(`/deals/by-contact-id/${contactId}`),
      ]);

      const refreshedLeads = Array.isArray(leadsResponse.data)
        ? leadsResponse.data
        : [];

      const refreshedDeals = Array.isArray(dealsResponse.data)
        ? dealsResponse.data
        : [];

      let createdDeal =
        responseDeal && typeof responseDeal === "object" && responseDeal.id
          ? responseDeal
          : null;

      if (!createdDeal) {
        createdDeal =
          refreshedDeals.find(
            (deal) => Number(deal.lead_id) === convertedLeadId,
          ) || null;
      }

      if (
        createdDeal?.id &&
        !refreshedDeals.some(
          (deal) => Number(deal.id) === Number(createdDeal.id),
        )
      ) {
        refreshedDeals.unshift(createdDeal);
      }

      setLeads(refreshedLeads);
      setDeals(refreshedDeals);
      setLeadsLoaded(true);
      setDealsLoaded(true);

      setSelectedLeadWorkId(null);
      setShowDealWorkItems(true);
      refreshAcademyWorkflow();
      setTopTab("works");

      if (createdDeal?.id) {
        setSelectedWorkId(createdDeal.id);
      } else {
        const latestConvertedDeal = refreshedDeals
          .filter((deal) => Number(deal.lead_id) === convertedLeadId)
          .sort(
            (a, b) =>
              new Date(b.created_at || b.updated_at || 0) -
              new Date(a.created_at || a.updated_at || 0),
          )[0];

        setSelectedWorkId(latestConvertedDeal?.id || null);
      }

      showToast(
        t("lead_converted_to_deal", {
          defaultValue: "Лид успешно конвертирован в сделку.",
        }),
      );
    } catch (error) {
      console.error("Failed to convert lead to deal", error);

      setLeadEditorError(
        error?.response?.data?.detail ||
          t("error_converting_lead", {
            defaultValue: "Не удалось конвертировать лид в сделку.",
          }),
      );
    } finally {
      setLeadConverting(false);
    }
  };

  const selectedLeadTimelineItems = selectedLeadWork
    ? [
        {
          key: `lead-created-${selectedLeadWork.id}`,
          kind: "lead",
          date: selectedLeadWork.created_at,
          title: t("lead_created", { defaultValue: "Лид создан" }),
          description:
            selectedLeadWork.description || selectedLeadWork.title || "",
          status: selectedLeadWork.status,
          icon: "L",
        },
        ...selectedLeadTasks.map((task) => ({
          key: `lead-task-${task.id}`,
          kind: "task",
          date:
            task.completed_at ||
            task.updated_at ||
            task.due_at ||
            task.created_at,
          title: task.title || t("sales_tasks", { defaultValue: "Задача" }),
          description: task.description || "",
          status: task.status,
          icon: String(task.status || "").toLowerCase() === "done" ? "✓" : "□",
        })),
        ...selectedLeadActivities.map((activity) => ({
          key: `lead-activity-${activity.id}`,
          kind: "activity",
          date:
            activity.when_at || activity.completed_at || activity.created_at,
          title:
            activity.subject ||
            activity.type ||
            t("sales_activities", { defaultValue: "Активность" }),
          description: activity.content || "",
          status: activity.status || activity.outcome || activity.type,
          icon:
            activity.type === "call"
              ? "☎"
              : activity.type === "email"
                ? "✉"
                : activity.type === "message"
                  ? "💬"
                  : "✎",
        })),
      ]
        .filter((item) => item.date)
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    : [];

  useEffect(() => {
    setWorkAiData(null);
    setWorkAiError(null);
    setWorkAiCopied(null);
  }, [selectedWork?.id]);

  const selectedWorkLead = selectedWork?.lead_id
    ? leads.find((lead) => Number(lead.id) === Number(selectedWork.lead_id)) ||
      null
    : null;

  const selectedWorkOriginTasks = selectedWork?.lead_id
    ? tasks
        .filter((task) => Number(task.lead_id) === Number(selectedWork.lead_id))
        .sort((a, b) => {
          const av = new Date(a.created_at || a.due_at || 0).getTime();
          const bv = new Date(b.created_at || b.due_at || 0).getTime();
          return av - bv;
        })
    : [];

  const selectedWorkOriginTask = selectedWorkOriginTasks[0] || null;

  const selectedWorkTasks = selectedWork ? getWorkTasks(selectedWork) : [];
  const selectedWorkActivities = selectedWork
    ? getWorkActivities(selectedWork)
    : [];
  const selectedWorkFiles = selectedWork ? getWorkFiles(selectedWork) : [];

  const selectedWorkCompletedTasks = selectedWorkTasks.filter(
    (task) => String(task.status || "").toLowerCase() === "done",
  ).length;

  const selectedWorkProgress =
    selectedWorkTasks.length > 0
      ? Math.round(
          (selectedWorkCompletedTasks / selectedWorkTasks.length) * 100,
        )
      : 0;

  const selectedWorkNextTask = [...selectedWorkTasks]
    .filter((task) => String(task.status || "").toLowerCase() !== "done")
    .sort((a, b) => {
      const av = a.due_at
        ? new Date(a.due_at).getTime()
        : Number.MAX_SAFE_INTEGER;
      const bv = b.due_at
        ? new Date(b.due_at).getTime()
        : Number.MAX_SAFE_INTEGER;
      return av - bv;
    })[0];

  const selectedWorkAmount = Number(selectedWork?.amount || 0);
  const selectedWorkPaid = Number(selectedWork?.prepayment || 0);
  const selectedWorkBalance = Number.isFinite(Number(selectedWork?.balance))
    ? Number(selectedWork?.balance)
    : Math.max(selectedWorkAmount - selectedWorkPaid, 0);

  const selectedWorkPaymentProgress =
    selectedWorkAmount > 0
      ? Math.min(
          100,
          Math.max(
            0,
            Math.round((selectedWorkPaid / selectedWorkAmount) * 100),
          ),
        )
      : selectedWorkPaid > 0
        ? 100
        : 0;

  const selectedWorkPaymentHistory = selectedWorkActivities
    .filter((activity) => {
      const subject = String(activity.subject || "").toLowerCase();
      const content = String(activity.content || "").toLowerCase();
      return (
        subject.includes("payment") ||
        subject.includes("оплат") ||
        content.includes("payment amount:")
      );
    })
    .map((activity) => {
      const content = String(activity.content || "");
      const amountMatch = content.match(
        /Payment amount:\s*([0-9]+(?:[.,][0-9]+)?)/i,
      );
      const methodMatch = content.match(/Payment method:\s*([^\n]+)/i);
      const noteMatch = content.match(/Payment note:\s*([^\n]+)/i);
      return {
        id: activity.id,
        amount: amountMatch ? Number(amountMatch[1].replace(",", ".")) : null,
        method: methodMatch?.[1]?.trim() || "",
        note: noteMatch?.[1]?.trim() || "",
        date: activity.when_at || activity.created_at,
      };
    })
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const selectedWorkPaymentStatus =
    selectedWorkAmount <= 0
      ? "not_set"
      : selectedWorkBalance <= 0
        ? "paid"
        : selectedWorkPaid > 0
          ? "partial"
          : "unpaid";

  const selectedWorkTimelineItems = selectedWork
    ? [
        {
          key: `work-created-${selectedWork.id}`,
          kind: "system",
          subtype: "system",
          date: selectedWork.created_at,
          title: t("work_created", { defaultValue: "Работа создана" }),
          description: selectedWork.description || "",
          status: selectedWork.status,
          icon: "◆",
          files: [],
          entity: selectedWork,
        },
        ...selectedWorkTasks.map((task) => ({
          key: `work-task-${task.id}`,
          kind: "task",
          subtype: "task",
          date:
            task.completed_at ||
            task.updated_at ||
            task.due_at ||
            task.created_at,
          title: task.title || t("sales_tasks", { defaultValue: "Задача" }),
          description: task.description || "",
          status: task.status,
          icon: String(task.status || "").toLowerCase() === "done" ? "✓" : "□",
          files: [],
          entity: task,
        })),
        ...selectedWorkActivities.map((activity) => {
          const type = String(activity.type || "note").toLowerCase();
          const files = Array.isArray(activity.file_attachments)
            ? activity.file_attachments
            : [];
          return {
            key: `work-activity-${activity.id}`,
            kind: "activity",
            subtype: files.length > 0 ? "file" : type,
            date:
              activity.when_at || activity.completed_at || activity.created_at,
            title:
              activity.subject ||
              (type === "call"
                ? t("call", { defaultValue: "Звонок" })
                : type === "email"
                  ? t("email", { defaultValue: "Письмо" })
                  : type === "message"
                    ? t("message", { defaultValue: "Сообщение" })
                    : type === "meeting"
                      ? t("meeting", { defaultValue: "Встреча" })
                      : t("note", { defaultValue: "Заметка" })),
            description: activity.content || "",
            status: activity.status || activity.outcome || type,
            icon:
              files.length > 0
                ? "▣"
                : type === "call"
                  ? "☎"
                  : type === "email"
                    ? "✉"
                    : type === "message"
                      ? "💬"
                      : type === "meeting"
                        ? "◉"
                        : "✎",
            files,
            entity: activity,
          };
        }),
        ...(selectedWork.completed_at
          ? [
              {
                key: `work-completed-${selectedWork.id}`,
                kind: "system",
                subtype: "system",
                date: selectedWork.completed_at,
                title: t("work_completed", {
                  defaultValue: "Работа завершена",
                }),
                description: selectedWork.work_summary || "",
                status: selectedWork.status,
                icon: "✓",
                files: selectedWorkFiles,
                entity: selectedWork,
              },
            ]
          : []),
      ]
        .filter((item) => item.date)
        .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0))
    : [];

  const filteredWorkTimelineItems =
    workTimelineFilter === "all"
      ? selectedWorkTimelineItems
      : selectedWorkTimelineItems.filter((item) => {
          if (workTimelineFilter === "activity")
            return item.kind === "activity";
          return item.subtype === workTimelineFilter;
        });

  const groupedWorkTimelineItems = filteredWorkTimelineItems.reduce(
    (groups, item) => {
      const date = new Date(item.date);
      const key = Number.isNaN(date.getTime())
        ? t("unknown_date", { defaultValue: "Без даты" })
        : date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
      return groups;
    },
    {},
  );

  const toggleWorkTimelineItem = (key) => {
    setExpandedWorkTimelineItems((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const openWorkTimelineItem = (item) => {
    if (item.kind === "task") return openContactTask(item.entity);
    if (item.kind === "activity") return openAllContactActivities();
    return openContactDeal(selectedWork);
  };

  const getWorkFileUrl = (file) =>
    file?.web_content_link ||
    file?.web_view_link ||
    file?.thumbnail_link ||
    "#";

  const openWorkPhotoPicker = (category = "during") => {
    if (!selectedWork?.id) return;
    setWorkPhotoUploadCategory(category);
    setWorkPhotoError(null);
    workPhotoInputRef.current?.click();
  };

  const uploadWorkPhotos = async (
    files,
    category = workPhotoUploadCategory,
  ) => {
    const selectedFiles = Array.from(files || []).filter((file) =>
      String(file.type || "").startsWith("image/"),
    );
    if (!selectedWork?.id || selectedFiles.length === 0) return;
    setWorkPhotoUploading(true);
    setWorkPhotoError(null);
    try {
      const uploaded = [];
      for (const file of selectedFiles) {
        const form = new FormData();
        const cleanName = String(file.name || "photo").replace(
          /^((before|during|after)[-_]{1,2})+/i,
          "",
        );
        form.append("file", file, `${category}__${cleanName}`);
        const response = await api.post("/auth/storage/files", form, {
          params: { entity_type: "deal", entity_id: selectedWork.id },
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(response.data);
      }
      setDealFiles((current) => [...uploaded, ...current]);
      setDealFilesLoaded(true);
    } catch (error) {
      console.error("Failed to upload work photos", error);
      setWorkPhotoError(
        error?.response?.data?.detail ||
          t("work_photo_upload_error", {
            defaultValue: "Не удалось загрузить фотографии.",
          }),
      );
    } finally {
      setWorkPhotoUploading(false);
      if (workPhotoInputRef.current) workPhotoInputRef.current.value = "";
    }
  };

  const handleWorkPhotoDrop = (event, category = "during") => {
    event.preventDefault();
    setWorkPhotoDragActive(false);
    setWorkPhotoUploadCategory(category);
    uploadWorkPhotos(event.dataTransfer.files, category);
  };

  const deleteWorkPhoto = (file) => {
    if (!file?.id) return;
    setConfirmAction({
      type: "delete-photo",
      file,
      title: t("delete_photo", { defaultValue: "Удалить фотографию?" }),
      message: t("confirm_delete_photo", {
        defaultValue: "Файл будет удалён из CRM и подключённого хранилища.",
      }),
      danger: true,
    });
  };

  const confirmDeleteWorkPhoto = async (file) => {
    try {
      await api.delete(`/auth/storage/files/${file.id}`);
      setDealFiles((current) =>
        current.filter((item) => Number(item.id) !== Number(file.id)),
      );
      if (Number(workPhotoLightbox?.id) === Number(file.id))
        setWorkPhotoLightbox(null);
      setConfirmAction(null);
      showToast(t("photo_deleted", { defaultValue: "Фотография удалена" }));
    } catch (error) {
      setConfirmAction(null);
      setWorkPhotoError(
        error?.response?.data?.detail ||
          t("work_photo_delete_error", {
            defaultValue: "Не удалось удалить фотографию.",
          }),
      );
      showToast(
        error?.response?.data?.detail ||
          t("work_photo_delete_error", {
            defaultValue: "Не удалось удалить фотографию.",
          }),
        "danger",
      );
    }
  };

  const classifyWorkFile = (file) => {
    const name = String(file?.file_name || "").toLowerCase();
    if (name.includes("before") || name.includes("до")) return "before";
    if (
      name.includes("during") ||
      name.includes("process") ||
      name.includes("процесс")
    )
      return "during";
    if (
      name.includes("after") ||
      name.includes("final") ||
      name.includes("после")
    )
      return "after";
    return "during";
  };

  const selectedWorkFileGroups = {
    before: selectedWorkFiles.filter(
      (file) => classifyWorkFile(file) === "before",
    ),
    during: selectedWorkFiles.filter(
      (file) => classifyWorkFile(file) === "during",
    ),
    after: selectedWorkFiles.filter(
      (file) => classifyWorkFile(file) === "after",
    ),
  };

  const activityFiles = activities.flatMap((activity) =>
    (Array.isArray(activity.file_attachments)
      ? activity.file_attachments
      : []
    ).map((file) => ({
      ...file,
      source_type: "activity",
      source_title:
        activity.subject ||
        activity.type ||
        t("sales_activities", { defaultValue: "Активность" }),
      source_date:
        activity.when_at || activity.completed_at || activity.created_at,
    })),
  );

  const directContactFiles = contactAttachments.map((file, index) => ({
    ...file,
    id: file.id || `contact-${index}`,
    file_name:
      file.file_name || file.filename || file.name || `File ${index + 1}`,
    source_type: "contact",
    source_title: t("sales_contacts", { defaultValue: "Контакт" }),
    source_date: file.created_at || contact?.created_at,
  }));

  const allContactFiles = [...activityFiles, ...directContactFiles].sort(
    (a, b) =>
      new Date(b.source_date || b.created_at || 0) -
      new Date(a.source_date || a.created_at || 0),
  );

  const documentFiles = allContactFiles.filter(
    (file) => !String(file.mime_type || "").startsWith("image/"),
  );

  const imageFiles = allContactFiles.filter((file) =>
    String(file.mime_type || "").startsWith("image/"),
  );

  useEffect(() => {
    if (topTab !== "works") return;

    const selectedDealStillVisible =
      showDealWorkItems &&
      activeWorks.some((deal) => Number(deal.id) === Number(selectedWorkId));

    const selectedLeadStillVisible =
      showLeadWorkItems &&
      activeLeadWorks.some(
        (lead) => Number(lead.id) === Number(selectedLeadWorkId),
      );

    if (selectedDealStillVisible) {
      if (selectedLeadWorkId !== null) setSelectedLeadWorkId(null);
      return;
    }

    if (selectedLeadStillVisible) {
      if (selectedWorkId !== null) setSelectedWorkId(null);
      return;
    }

    if (showLeadWorkItems && activeLeadWorks.length > 0) {
      setSelectedLeadWorkId(activeLeadWorks[0].id);
      setSelectedWorkId(null);
      return;
    }

    if (showDealWorkItems && activeWorks.length > 0) {
      setSelectedWorkId(activeWorks[0].id);
      setSelectedLeadWorkId(null);
      return;
    }

    if (selectedWorkId !== null) setSelectedWorkId(null);
    if (selectedLeadWorkId !== null) setSelectedLeadWorkId(null);
  }, [
    topTab,
    deals,
    leads,
    selectedWorkId,
    selectedLeadWorkId,
    showLeadWorkItems,
    showDealWorkItems,
  ]);

  const timelineItems = [
    ...activities.map((activity) => ({
      key: `activity-${activity.id}`,
      kind: "activity",
      date: activity.when_at || activity.completed_at || activity.created_at,
      title:
        activity.subject ||
        activity.type ||
        t("sales_activities", { defaultValue: "Активность" }),
      description: activity.content || "",
      status: activity.status || activity.outcome || activity.type,
      icon:
        activity.type === "call"
          ? "☎"
          : activity.type === "email"
            ? "✉"
            : activity.type === "message"
              ? "💬"
              : activity.type === "meeting"
                ? "◉"
                : "•",
      entity: activity,
      files: Array.isArray(activity.file_attachments)
        ? activity.file_attachments
        : [],
    })),
    ...tasks.map((task) => ({
      key: `task-${task.id}`,
      kind: "task",
      date: task.completed_at || task.due_at || task.created_at,
      title: task.title || t("sales_tasks", { defaultValue: "Задача" }),
      description: task.description || "",
      status: task.status,
      icon: String(task.status || "").toLowerCase() === "done" ? "✓" : "□",
      entity: task,
      files: [],
    })),
    ...leads.map((lead) => ({
      key: `lead-${lead.id}`,
      kind: "lead",
      date: lead.updated_at || lead.created_at,
      title:
        lead.title ||
        `${t("sales_leads", { defaultValue: "Лид" })} #${
          lead.ui_id ?? lead.id
        }`,
      description: lead.description || "",
      status: lead.status,
      icon: "L",
      entity: lead,
      files: [],
    })),
    ...deals.map((deal) => ({
      key: `deal-${deal.id}`,
      kind: "deal",
      date: deal.updated_at || deal.created_at,
      title:
        deal.title ||
        `${t("sales_deals", { defaultValue: "Сделка" })} #${
          deal.ui_id ?? deal.id
        }`,
      description:
        deal.status === "won"
          ? t("deal_completed_successfully", {
              defaultValue: "Сделка успешно завершена",
            })
          : deal.status === "lost"
            ? t("deal_closed_lost", {
                defaultValue: "Сделка закрыта как проигранная",
              })
            : "",
      status: deal.status,
      icon: "€",
      entity: deal,
      files: getWorkFiles(deal),
    })),
  ]
    .filter((item) => item.date)
    .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const filteredTimelineItems =
    timelineFilter === "all"
      ? timelineItems
      : timelineItems.filter((item) => item.kind === timelineFilter);

  const groupedTimelineItems = filteredTimelineItems.reduce((groups, item) => {
    const date = new Date(item.date);
    const key = Number.isNaN(date.getTime())
      ? t("unknown_date", { defaultValue: "Без даты" })
      : date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
    return groups;
  }, {});

  const openTimelineItem = (item) => {
    if (item.kind === "task") return openContactTask(item.entity);
    if (item.kind === "lead") return openContactLead(item.entity);
    if (item.kind === "deal") return openContactDeal(item.entity);
    return openAllContactActivities();
  };

  return (
    <>
      <div className="container-fluid py-3 personalDetailsPage">
        {contactLoading && (
          <div
            className="pdPageSkeleton"
            aria-label={t("loading", { defaultValue: "Загрузка" })}
          >
            <div className="pdSkeletonHero">
              <span />
              <div>
                <i />
                <i />
              </div>
            </div>
            <div className="pdSkeletonKpis">
              {Array.from({ length: 6 }).map((_, index) => (
                <i key={index} />
              ))}
            </div>
            <div className="pdSkeletonBody">
              <i />
              <i />
              <i />
            </div>
          </div>
        )}
        <section
          data-academy="personal-header"
          className="pdContactOverviewHero"
        >
          <div className="pdContactHeroMain">
            <div className="pdContactHeroIdentity">
              <ContactAvatar name={contact?.full_name} />
              <div className="pdContactHeroText">
                <div className="pdContactHeroTitle">
                  <h4>
                    {contactLoading
                      ? t("contact_loading")
                      : contact?.full_name || t("contact_fallback_name")}
                  </h4>
                  <span
                    className={`pdLifecycleBadge ${statusClass(lifecycleStage)}`}
                  >
                    {lifecycleStage ? t(lifecycleStage, lifecycleStage) : "--"}
                  </span>
                </div>
                <div className="pdContactHeroChannels">
                  {primaryPhone && (
                    <a href={`tel:${primaryPhone}`}>
                      <i className="lni lni-phone" />
                      {primaryPhone}
                    </a>
                  )}
                  {telegramLabel && (
                    <span>
                      <i className="lni lni-telegram" />
                      {telegramLabel}
                    </span>
                  )}
                  {primaryEmail && (
                    <a href={`mailto:${primaryEmail}`}>
                      <i className="lni lni-envelope" />
                      {primaryEmail}
                    </a>
                  )}
                </div>
                <div className="pdContactHeroRelations">
                  <span>
                    <i className="lni lni-user" />
                    {t("contact_owner", {
                      defaultValue: "Ответственный",
                    })}: <b>{ownerName}</b>
                  </span>
                  <button
                    type="button"
                    onClick={
                      company?.id ? openCompanyDetails : openCompanyModal
                    }
                  >
                    <i className="lni lni-apartment" />
                    {t("sales_contacts_company", {
                      defaultValue: "Компания",
                    })}:{" "}
                    <b>
                      {company?.firm_name ||
                        t("contact_no_company_assigned", {
                          defaultValue: "Не назначена",
                        })}
                    </b>
                  </button>
                </div>
              </div>
            </div>

            <div className="pdContactHeroActions">
              {primaryPhone && (
                <a
                  className="pdHeroAction pdHeroAction--call"
                  href={`tel:${primaryPhone}`}
                >
                  <i className="lni lni-phone" />
                  {t("call", { defaultValue: "Позвонить" })}
                </a>
              )}
              <button
                className="pdHeroAction pdHeroAction--task"
                type="button"
                onClick={createTaskForContact}
              >
                <i className="lni lni-checkmark-circle" />
                {t("contact_new_task", { defaultValue: "Новая задача" })}
              </button>
              <button
                className="pdHeroAction"
                type="button"
                onClick={openEditContact}
              >
                <i className="lni lni-pencil" />
                {t("edit")}
              </button>
              <div className="position-relative">
                <button
                  data-academy="personal-more"
                  className="pdHeroMore"
                  type="button"
                  onClick={() => setShowHeaderMenu((v) => !v)}
                  aria-label={t("actions")}
                >
                  <i className="lni lni-menu-cheesburger" />
                </button>
                {showHeaderMenu && (
                  <div className="dropdown-menu dropdown-menu-end show pdContactMenu">
                    <button
                      data-academy="personal-create-lead"
                      className="dropdown-item"
                      type="button"
                      onClick={createLeadForContact}
                    >
                      {t("create_lead", { defaultValue: "Создать лид" })}
                    </button>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={createDealForContact}
                    >
                      {t("contact_deal", { defaultValue: "Создать сделку" })}
                    </button>
                    <button
                      className="dropdown-item"
                      type="button"
                      onClick={openCompanyModal}
                    >
                      {company
                        ? t("contact_change", {
                            defaultValue: "Изменить компанию",
                          })
                        : t("contact_assign_company", {
                            defaultValue: "Назначить компанию",
                          })}
                    </button>
                    <div className="dropdown-divider" />
                    <button
                      className="dropdown-item text-danger"
                      type="button"
                      onClick={handleDeleteContact}
                    >
                      {t("delete")}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div data-academy="personal-kpi" className="pdContactHeroKpis">
            <button type="button" onClick={() => setTopTab("works")}>
              <span className="pdHeroKpiIcon pdHeroKpiIcon--work">
                <i className="lni lni-briefcase-2" />
              </span>
              <span>
                <small>
                  {t("active_works", { defaultValue: "Активные работы" })}
                </small>
                <b>{openDeals.length}</b>
                <em>
                  {openDeals.length
                    ? `${openDeals.length} ${t("in_progress", { defaultValue: "в процессе" })}`
                    : t("none", { defaultValue: "Нет активных" })}
                </em>
              </span>
            </button>
            <button type="button" onClick={openAllContactTasks}>
              <span className="pdHeroKpiIcon pdHeroKpiIcon--task">
                <i className="lni lni-check-square-2" />
              </span>
              <span>
                <small>
                  {t("open_tasks", { defaultValue: "Открытые задачи" })}
                </small>
                <b>{openTasks.length}</b>
                <em>
                  {
                    openTasks.filter(
                      (task) =>
                        String(task.status || "").toLowerCase() === "overdue",
                    ).length
                  }{" "}
                  {t("overdue", { defaultValue: "просрочено" })}
                </em>
              </span>
            </button>
            <div>
              <span className="pdHeroKpiIcon pdHeroKpiIcon--money">
                <i className="lni lni-wallet-1" />
              </span>
              <span>
                <small>
                  {t("amount_due", { defaultValue: "Сумма к оплате" })}
                </small>
                <b>{balanceDue.toFixed(2)} €</b>
                <em>
                  {t("of_total", { defaultValue: "из" })}{" "}
                  {totalAmount.toFixed(2)} €
                </em>
              </span>
            </div>
            <button type="button" onClick={openAllContactActivities}>
              <span className="pdHeroKpiIcon pdHeroKpiIcon--activity">
                <i className="lni lni-watch-beat-1" />
              </span>
              <span>
                <small>
                  {t("contact_last_contacted", {
                    defaultValue: "Последняя активность",
                  })}
                </small>
                <b className="pdHeroKpiDate">
                  {formatDateShort(
                    latestActivity?.when_at ||
                      latestActivity?.completed_at ||
                      latestActivity?.created_at,
                  )}
                </b>
                <em>
                  {latestActivity?.channel || latestActivity?.type || "--"}
                </em>
              </span>
            </button>
          </div>
        </section>

        <nav
          data-academy="personal-tabs"
          className="personalDetailsTabs pdWorkspaceTabs"
          role="tablist"
          aria-label={t("contact_fallback_name")}
        >
          {[
            {
              key: "about",
              label: t("contact_tab_about", { defaultValue: "Обзор" }),
              count: null,
            },
            {
              key: "activities",
              label: t("contact_tab_activities", { defaultValue: "История" }),
              count: activitiesCount,
            },
            {
              key: "works",
              label: t("works", { defaultValue: "Работы" }),
              count: dealsCount,
            },
            {
              key: "documents",
              label: t("documents", { defaultValue: "Файлы" }),
              count: allContactFiles.length,
            },
            {
              key: "revenue",
              label: t("analytics", { defaultValue: "Аналитика" }),
              count: null,
            },
            { key: "intelligence", label: "AI", count: null },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              data-academy-tab={tab.key}
              role="tab"
              aria-selected={topTab === tab.key}
              className={`pdWorkspaceTab ${topTab === tab.key ? "is-active" : ""}`}
              onClick={() => setTopTab(tab.key)}
            >
              <span>{tab.label}</span>
              {tab.count !== null && <b>{tab.count}</b>}
            </button>
          ))}
        </nav>

        {topTab === "about" && (
          <section data-academy="personal-content" className="pdAboutDashboard">
            <div className="pdAboutColumn pdAboutColumn--profile">
              <article className="pdAboutCard pdAboutProfileCard">
                <header>
                  <h6>
                    {t("contact_tab_about", { defaultValue: "О контакте" })}
                  </h6>
                </header>
                <div className="pdAboutInfoList">
                  <div>
                    <i className="lni lni-buildings-1"></i>
                    <span>
                      <small>
                        {t("sales_contacts_company", {
                          defaultValue: "Компания",
                        })}
                      </small>
                      <b>
                        {companyLoading
                          ? t("loading")
                          : company?.firm_name ||
                            t("contact_no_company_assigned", {
                              defaultValue: "Не назначена",
                            })}
                      </b>
                    </span>
                    <button type="button" onClick={openCompanyModal}>
                      {company
                        ? t("contact_change", { defaultValue: "Изменить" })
                        : t("contact_assign", { defaultValue: "Назначить" })}
                    </button>
                  </div>
                  <div>
                    <i className="lni lni-envelope-1" />
                    <span>
                      <small>{t("email")}</small>
                      <b>{primaryEmail || "--"}</b>
                    </span>
                    {!primaryEmail && (
                      <button type="button" onClick={openEditContact}>
                        {t("add", { defaultValue: "Добавить" })}
                      </button>
                    )}
                  </div>
                  <div>
                    <i className="lni lni-phone" />
                    <span>
                      <small>{t("phone")}</small>
                      <b>{primaryPhone || "--"}</b>
                    </span>
                    <button type="button" onClick={openEditContact}>
                      {t("edit", { defaultValue: "Изменить" })}
                    </button>
                  </div>
                  <div className="pdAboutTelegramRow">
                    <i className="lni lni-telegram" />
                    <span>
                      <small>{t("source_preset_telegram")}</small>
                      <b>{telegramUsernameLabel}</b>
                    </span>
                    <button
                      className="pdTelegramEditTrigger"
                      type="button"
                      onClick={openTelegramEditor}
                    >
                      <i className="lni lni-pencil" />
                      {telegramUsername
                        ? t("edit", { defaultValue: "Изменить" })
                        : t("add", { defaultValue: "Добавить" })}
                    </button>
                  </div>
                  <div>
                    <i className="lni lni-user-4" />
                    <span>
                      <small>
                        {t("contact_owner", { defaultValue: "Ответственный" })}
                      </small>
                      <b>{ownerName}</b>
                    </span>
                  </div>
                  <div>
                    <i className="lni lni-target-user" />
                    <span>
                      <small>
                        {t("contact_lifecycle_stage", {
                          defaultValue: "Статус контакта",
                        })}
                      </small>
                      <b>
                        <span
                          className={`pdInlineStatus ${statusClass(lifecycleStage)}`}
                        >
                          {lifecycleStage
                            ? t(lifecycleStage, lifecycleStage)
                            : "--"}
                        </span>
                      </b>
                    </span>
                  </div>
                  <div>
                    <i className="lni lni-calendar-days" />
                    <span>
                      <small>
                        {t("created_at", { defaultValue: "Дата создания" })}
                      </small>
                      <b>{formatDateShort(contact?.created_at)}</b>
                    </span>
                  </div>
                  <div>
                    <i className="lni lni-watch-beat-1" />
                    <span>
                      <small>
                        {t("contact_last_contacted", {
                          defaultValue: "Последний контакт",
                        })}
                      </small>
                      <b>
                        {formatDateShort(
                          lastContacted ||
                            latestActivity?.when_at ||
                            latestActivity?.created_at,
                        )}
                        {latestActivity?.channel
                          ? ` (${latestActivity.channel})`
                          : ""}
                      </b>
                    </span>
                  </div>
                  <div>
                    <i className="lni lni-comment-1-text"></i>
                    <span>
                      <small>{t("notes", { defaultValue: "Заметки" })}</small>
                      <b>{contact?.notes || "--"}</b>
                    </span>
                    {!contact?.notes && (
                      <button type="button" onClick={openEditContact}>
                        {t("add", { defaultValue: "Добавить" })}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            </div>

            <div className="pdAboutColumn pdAboutColumn--work">
              <article className="pdAboutCard">
                <header>
                  <h6>
                    {t("active_works", { defaultValue: "Активные работы" })}
                  </h6>
                  <button type="button" onClick={() => setTopTab("works")}>
                    {t("open_all", { defaultValue: "Все работы" })} →
                  </button>
                </header>
                {openDeals.length ? (
                  <div className="pdAboutWorkList">
                    {openDeals.slice(0, 2).map((deal) => {
                      const dealTasks = getWorkTasks(deal);
                      const done = dealTasks.filter(
                        (task) =>
                          String(task.status || "").toLowerCase() === "done",
                      ).length;
                      const progress = dealTasks.length
                        ? Math.round((done / dealTasks.length) * 100)
                        : 0;
                      const amount = Number(deal.amount || 0);
                      const paid = Number(deal.prepayment || 0);
                      const due = Number.isFinite(Number(deal.balance))
                        ? Number(deal.balance)
                        : Math.max(amount - paid, 0);
                      return (
                        <button
                          key={deal.id}
                          type="button"
                          className="pdAboutWorkItem"
                          onClick={() => {
                            setSelectedWorkId(deal.id);
                            setTopTab("works");
                          }}
                        >
                          <span className="pdAboutWorkIcon">
                            <i className="lni lni-briefcase-2" />
                          </span>
                          <span className="pdAboutWorkMain">
                            <b>
                              {deal.title ||
                                `${t("contact_deal", { defaultValue: "Сделка" })} #${deal.ui_id ?? deal.id}`}
                            </b>
                            <small>
                              {t("deadline", { defaultValue: "Дедлайн" })}:{" "}
                              {formatDateShort(deal.due_at || deal.updated_at)}{" "}
                              · {t("budget", { defaultValue: "Бюджет" })}:{" "}
                              {amount ? `${amount.toFixed(2)} €` : "--"}
                            </small>
                            <span className="pdAboutProgress">
                              <i style={{ width: `${progress}%` }} />
                            </span>
                          </span>
                          <span className="pdAboutWorkSide">
                            <strong>{due.toFixed(2)} €</strong>
                            <small>
                              {t("balance_due", { defaultValue: "К оплате" })}
                            </small>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="pdAboutEmpty">
                    <span>
                      {t("contact_no_deals_associated", {
                        defaultValue: "Активных работ пока нет",
                      })}
                    </span>
                    <button type="button" onClick={createDealForContact}>
                      +{" "}
                      {t("contact_create_deal_prompt", {
                        defaultValue: "Создать работу",
                      })}
                    </button>
                  </div>
                )}
              </article>

              <article className="pdAboutCard">
                <header>
                  <h6>{t("sales_tasks", { defaultValue: "Задачи" })}</h6>
                  <button type="button" onClick={openAllContactTasks}>
                    {t("open_all", { defaultValue: "Все задачи" })} →
                  </button>
                </header>
                {openTasks.length ? (
                  <div className="pdAboutTaskList">
                    {openTasks.slice(0, 3).map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => openContactTask(task)}
                      >
                        <span className="pdAboutTaskCheck" />
                        <span>
                          <b>{task.title}</b>
                          <small>
                            {formatDateShort(task.due_at)} ·{" "}
                            {task.owner_full_name || ownerName}
                          </small>
                        </span>
                        <em className={statusClass(task.status)}>
                          {task.status ? t(task.status, task.status) : "--"}
                        </em>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="pdAboutEmpty">
                    <span>
                      {t("contact_no_next_action", {
                        defaultValue: "Нет открытых задач",
                      })}
                    </span>
                    <button type="button" onClick={createTaskForContact}>
                      +{" "}
                      {t("contact_new_task", { defaultValue: "Новая задача" })}
                    </button>
                  </div>
                )}
                <footer>
                  <button type="button" onClick={createTaskForContact}>
                    + {t("contact_new_task", { defaultValue: "Новая задача" })}
                  </button>
                </footer>
              </article>

              <article className="pdAboutCard pdAboutNextCard">
                <header>
                  <h6>
                    {t("contact_next_action", {
                      defaultValue: "Ближайшее действие",
                    })}
                  </h6>
                </header>
                {nextOpenTask ? (
                  <button
                    type="button"
                    className="pdAboutNextAction"
                    onClick={() => openContactTask(nextOpenTask)}
                  >
                    <span>
                      <i className="lni lni-phone" />
                    </span>
                    <span>
                      <b>{nextOpenTask.title}</b>
                      <small>{getTaskShortDescription(nextOpenTask)}</small>
                      <em>{formatDate(nextOpenTask.due_at)}</em>
                    </span>
                  </button>
                ) : (
                  <div className="pdAboutEmpty">
                    <span>
                      {t("contact_no_next_action", {
                        defaultValue: "Ближайшее действие не запланировано",
                      })}
                    </span>
                    <button type="button" onClick={createTaskForContact}>
                      + {t("add", { defaultValue: "Добавить" })}
                    </button>
                  </div>
                )}
              </article>
            </div>

            <div className="pdAboutColumn pdAboutColumn--insights">
              <article className="pdAboutCard pdAboutAiCard">
                <header>
                  <h6>
                    {t("contact_ai_summary", { defaultValue: "AI сводка" })}
                  </h6>
                  <button type="button" onClick={handleGenerateSummary}>
                    {aiSummary
                      ? t("contact_regenerate", { defaultValue: "Обновить" })
                      : t("generate", { defaultValue: "Сгенерировать" })}
                  </button>
                </header>
                <div className="pdAboutAiBody">
                  {aiLoading && (
                    <div className="pdAboutLoading">
                      <span />
                      <span />
                      <span />
                    </div>
                  )}
                  {aiError && (
                    <div className="alert alert-danger py-2 mb-0">
                      {aiError}
                    </div>
                  )}
                  {!aiLoading && !aiError && aiSummary && (
                    <>
                      <p>{aiSummary.summary}</p>
                      {Array.isArray(aiSummary.highlights) &&
                        aiSummary.highlights.length > 0 && (
                          <div className="pdAboutAiTags">
                            {aiSummary.highlights
                              .slice(0, 3)
                              .map((item, idx) => (
                                <span key={idx}>{compactText(item, 32)}</span>
                              ))}
                          </div>
                        )}
                    </>
                  )}
                  {!aiLoading && !aiError && !aiSummary && (
                    <div className="pdAboutEmpty">
                      <span>
                        {t("contact_generate_summary_hint", {
                          defaultValue:
                            "Сформируйте краткую сводку по клиенту и его текущим обращениям.",
                        })}
                      </span>
                      <button type="button" onClick={handleGenerateSummary}>
                        {t("contact_generate_summary", {
                          defaultValue: "Сгенерировать сводку",
                        })}
                      </button>
                    </div>
                  )}
                </div>
              </article>

              <article className="pdAboutCard">
                <header>
                  <h6>
                    {t("recent_activities", {
                      defaultValue: "Последние активности",
                    })}
                  </h6>
                  <button type="button" onClick={openAllContactActivities}>
                    {t("open_all", { defaultValue: "Все активности" })} →
                  </button>
                </header>
                {activities.length ? (
                  <div className="pdAboutActivityList">
                    {activities.slice(0, 4).map((activity) => (
                      <button
                        key={activity.id}
                        type="button"
                        onClick={openAllContactActivities}
                      >
                        <span
                          className={`pdAboutActivityIcon pdAboutActivityIcon--${activity.type || "note"}`}
                        >
                          <i
                            className={
                              activity.type === "call"
                                ? "lni lni-phone"
                                : activity.type === "email"
                                  ? "lni lni-envelope"
                                  : activity.type === "message"
                                    ? "lni lni-telegram"
                                    : "lni lni-checkmark-circle"
                            }
                          />
                        </span>
                        <span>
                          <b>
                            {activity.subject ||
                              activity.type ||
                              t("sales_activities", {
                                defaultValue: "Активность",
                              })}
                          </b>
                          <small>{compactText(activity.content, 54)}</small>
                        </span>
                        <time>
                          {formatDateShort(
                            activity.when_at || activity.created_at,
                          )}
                          <br />
                          {new Date(
                            activity.when_at || activity.created_at || 0,
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </time>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="pdAboutEmpty">
                    <span>
                      {t("contact_no_activities", {
                        defaultValue: "Активностей пока нет",
                      })}
                    </span>
                  </div>
                )}
                <footer>
                  <button type="button" onClick={openAllContactActivities}>
                    +{" "}
                    {t("add_activity", { defaultValue: "Добавить активность" })}
                  </button>
                </footer>
              </article>
            </div>
          </section>
        )}

        {topTab === "works" && (
          <section data-academy="personal-works" className="pdWorksWorkspace">
            <div className="pdWorksHeader">
              <div>
                <h5>{t("works", { defaultValue: "Работы клиента" })}</h5>
                <p>
                  {t("contact_works_hint", {
                    defaultValue:
                      "Текущие работы и компактный архив завершённых сделок.",
                  })}
                </p>
              </div>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={createDealForContact}
              >
                +{" "}
                {t("contact_create_deal_prompt", {
                  defaultValue: "Новая работа",
                })}
              </button>
            </div>

            <div className="pdWorksMasterDetail">
              {selectedLeadWork && (
                <div
                  data-academy="lead-workspace"
                  data-academy-entity-title={selectedLeadWork.title || ""}
                  className="pdLeadWorkspace"
                >
                  <header className="pdLeadWorkspaceHeader">
                    <div className="pdLeadWorkspaceIdentity">
                      <span className="pdEntityTypeBadge pdEntityTypeBadge--lead">
                        {t("lead", { defaultValue: "ЛИД" })}
                      </span>
                      <div>
                        <h4>{selectedLeadWork.title || "--"}</h4>
                        <p>
                          {t("lead", { defaultValue: "Лид" })} #
                          {selectedLeadWork.ui_id ?? selectedLeadWork.id} ·{" "}
                          {formatDateShort(selectedLeadWork.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="pdLeadWorkspaceHeaderActions">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openContactLead(selectedLeadWork)}
                      >
                        {t("open_full", {
                          defaultValue: "Открыть полностью",
                        })}
                      </button>

                      <button
                        type="button"
                        data-academy="lead-save"
                        className="btn btn-sm btn-primary"
                        disabled={!leadEditorDirty || leadEditorSaving}
                        onClick={saveSelectedLeadWorkspace}
                      >
                        {leadEditorSaving
                          ? t("saving", { defaultValue: "Сохранение..." })
                          : t("save_changes", {
                              defaultValue: "Сохранить изменения",
                            })}
                      </button>
                    </div>
                  </header>

                  {leadEditorError && (
                    <div className="pdLeadWorkspaceError">
                      {leadEditorError}
                    </div>
                  )}

                  <section className="pdLeadWorkspaceCore">
                    <div className="pdLeadWorkspaceField">
                      <label htmlFor="pdLeadStatus">
                        {t("lead_status", { defaultValue: "Статус" })}
                      </label>
                      <select
                        id="pdLeadStatus"
                        value={leadEditorDraft.status}
                        onChange={(event) =>
                          updateLeadEditorDraft("status", event.target.value)
                        }
                      >
                        {LEAD_WORKSPACE_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {getLeadStatusLabel(t, status)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="pdLeadWorkspaceField pdLeadWorkspaceField--readonly">
                      <label>
                        {t("lead_source", { defaultValue: "Источник" })}
                      </label>
                      <div>
                        {getLeadSourceLabel(
                          t,
                          selectedLeadWork.source_name ||
                            selectedLeadWork.source,
                        )}
                      </div>
                    </div>

                    <div className="pdLeadWorkspaceField pdLeadWorkspaceField--readonly">
                      <label>
                        {t("sales_leads_contact", {
                          defaultValue: "Контакт",
                        })}
                      </label>
                      <button
                        type="button"
                        className="pdLeadContactLink"
                        onClick={() =>
                          navigate(
                            `/menu/sales/contacts?contact_id=${contactId}`,
                          )
                        }
                      >
                        <strong>
                          {contact?.full_name ||
                            contact?.name ||
                            contact?.display_name ||
                            `#${contact?.ui_id || contactId}`}
                        </strong>
                        <small>
                          {contact?.primary_phone ||
                            contact?.phone ||
                            selectedLeadWork.contact_primary_phone ||
                            "--"}
                        </small>
                      </button>
                    </div>

                    <div className="pdLeadWorkspaceField pdLeadWorkspaceField--readonly">
                      <label>{t("phone", { defaultValue: "Телефон" })}</label>
                      {contact?.primary_phone ||
                      contact?.phone ||
                      selectedLeadWork.contact_primary_phone ? (
                        <a
                          href={`tel:${
                            contact?.primary_phone ||
                            contact?.phone ||
                            selectedLeadWork.contact_primary_phone
                          }`}
                        >
                          {contact?.primary_phone ||
                            contact?.phone ||
                            selectedLeadWork.contact_primary_phone}
                        </a>
                      ) : (
                        <div>--</div>
                      )}
                    </div>
                  </section>

                  {!selectedLeadHasTask && (
                    <section
                      className="pdLeadTaskStage"
                      data-academy="lead-task-stage"
                    >
                      <div className="pdLeadWorkspaceSectionTitle">
                        <span>
                          {t("lead_task_first", {
                            defaultValue: "Сначала — задача",
                          })}
                        </span>
                        <small>
                          {t("task_required_before_terms", {
                            defaultValue: "Перед бюджетом и сделкой",
                          })}
                        </small>
                      </div>

                      <button
                        type="button"
                        data-academy="lead-create-task"
                        className="pdLeadCreateTaskCard"
                        onClick={createTaskForSelectedLead}
                      >
                        <span>+</span>
                        <div>
                          <strong>
                            {t("create_task", {
                              defaultValue: "Создать задачу",
                            })}
                          </strong>
                          <small>
                            {t("create_task_before_commercial_terms", {
                              defaultValue:
                                "Зафиксируйте следующий контакт с клиентом. После сохранения Task откроется шаг с коммерческими условиями.",
                            })}
                          </small>
                        </div>
                      </button>
                    </section>
                  )}

                  <section
                    data-academy="lead-finance"
                    data-academy-finance-ready={
                      Number(leadEditorDraft.budget) > 0 &&
                      Number(leadEditorDraft.prepayment) > 0
                        ? "true"
                        : "false"
                    }
                    className="pdLeadWorkspaceFinance"
                  >
                    <div className="pdLeadWorkspaceFinanceHeader">
                      <div>
                        <span>
                          {t("lead_finance", {
                            defaultValue: "Финансы лида",
                          })}
                        </span>
                        <strong>
                          {selectedLeadFinanceLockedByAcademy
                            ? t("academy_task_before_finance", {
                                defaultValue:
                                  "Сначала создайте связанную Task. Затем укажите бюджет и предоплату.",
                              })
                            : t("lead_finance_hint", {
                                defaultValue:
                                  "Бюджет и предоплата редактируются вручную, остаток рассчитывается автоматически.",
                              })}
                        </strong>
                      </div>
                    </div>

                    <div className="pdLeadWorkspaceFinanceGrid">
                      <label>
                        <span>
                          {t("lead_budget", {
                            defaultValue: "Бюджет",
                          })}
                        </span>
                        <div className="pdLeadMoneyInput">
                          <input
                            data-academy="lead-budget"
                            type="number"
                            disabled={selectedLeadFinanceLockedByAcademy}
                            min="0"
                            step="0.01"
                            value={leadEditorDraft.budget}
                            onChange={(event) =>
                              updateLeadEditorDraft(
                                "budget",
                                event.target.value,
                              )
                            }
                            placeholder="0.00"
                          />
                          <b>€</b>
                        </div>
                      </label>

                      <label>
                        <span>
                          {t("lead_prepayment", {
                            defaultValue: "Предоплата",
                          })}
                        </span>
                        <div className="pdLeadMoneyInput">
                          <input
                            data-academy="lead-prepayment"
                            type="number"
                            disabled={selectedLeadFinanceLockedByAcademy}
                            min="0"
                            step="0.01"
                            value={leadEditorDraft.prepayment}
                            onChange={(event) =>
                              updateLeadEditorDraft(
                                "prepayment",
                                event.target.value,
                              )
                            }
                            placeholder="0.00"
                          />
                          <b>€</b>
                        </div>
                      </label>

                      <div className="pdLeadBalanceCard">
                        <span>
                          {t("lead_balance_due", {
                            defaultValue: "К оплате",
                          })}
                        </span>
                        <strong>
                          {selectedLeadDraftBalance === null
                            ? "--"
                            : `${selectedLeadDraftBalance.toFixed(2)} €`}
                        </strong>
                        <small>
                          {t("calculated_automatically", {
                            defaultValue: "Рассчитывается автоматически",
                          })}
                        </small>
                      </div>
                    </div>
                  </section>

                  <section className="pdLeadWorkspaceMain">
                    <div className="pdLeadWorkspaceLeft">
                      <section className="pdLeadWorkspaceDescription">
                        <div className="pdLeadWorkspaceSectionTitle">
                          <span>
                            {t("description", {
                              defaultValue: "Описание лида",
                            })}
                          </span>
                          <small>
                            {t("editable", {
                              defaultValue: "Можно редактировать",
                            })}
                          </small>
                        </div>

                        <textarea
                          data-academy="lead-description"
                          rows={5}
                          value={leadEditorDraft.description}
                          onChange={(event) =>
                            updateLeadEditorDraft(
                              "description",
                              event.target.value,
                            )
                          }
                          placeholder={t("lead_description_placeholder", {
                            defaultValue:
                              "Опишите запрос клиента, договорённости и важные детали.",
                          })}
                        />
                      </section>

                      {selectedLeadHasTask && (
                        <section className="pdLeadTaskStage">
                          <div className="pdLeadWorkspaceSectionTitle">
                            <span>
                              {t("lead_task_stage", {
                                defaultValue: "Следующий шаг — задача",
                              })}
                            </span>
                            <small>
                              {t("task_created", {
                                defaultValue: "Задача создана",
                              })}
                            </small>
                          </div>

                          {selectedLeadPrimaryTask && (
                            <button
                              type="button"
                              data-academy="lead-create-task"
                              className="pdLeadTaskCard"
                              onClick={openSelectedLeadTask}
                            >
                              <span className="pdLeadTaskCardIcon">T</span>
                              <span>
                                <strong>
                                  {selectedLeadPrimaryTask.title ||
                                    `${t("task", {
                                      defaultValue: "Задача",
                                    })} #${
                                      selectedLeadPrimaryTask.ui_id ||
                                      selectedLeadPrimaryTask.id
                                    }`}
                                </strong>
                                <small>
                                  {selectedLeadPrimaryTask.description ||
                                    formatDate(
                                      selectedLeadPrimaryTask.due_at ||
                                        selectedLeadPrimaryTask.created_at,
                                    )}
                                </small>
                              </span>
                              <b>→</b>
                            </button>
                          )}
                        </section>
                      )}

                      <section className="pdLeadConversionStage">
                        <div className="pdLeadConversionStatus">
                          <div
                            className={
                              selectedLeadHasTask ? "is-complete" : "is-pending"
                            }
                          >
                            <span>{selectedLeadHasTask ? "✓" : "1"}</span>
                            <div>
                              <strong>
                                {t("task", {
                                  defaultValue: "Задача",
                                })}
                              </strong>
                              <small>
                                {selectedLeadHasTask
                                  ? t("created", {
                                      defaultValue: "Создана",
                                    })
                                  : t("required", {
                                      defaultValue: "Нужно создать",
                                    })}
                              </small>
                            </div>
                          </div>

                          <i aria-hidden="true">→</i>

                          <div
                            className={
                              selectedLeadHasBudget
                                ? "is-complete"
                                : "is-pending"
                            }
                          >
                            <span>{selectedLeadHasBudget ? "✓" : "2"}</span>
                            <div>
                              <strong>
                                {t("lead_budget", {
                                  defaultValue: "Бюджет",
                                })}
                              </strong>
                              <small>
                                {selectedLeadHasBudget
                                  ? t("completed", {
                                      defaultValue: "Заполнен",
                                    })
                                  : t("required", {
                                      defaultValue: "Нужно заполнить",
                                    })}
                              </small>
                            </div>
                          </div>

                          <i aria-hidden="true">→</i>

                          <div
                            className={
                              selectedLeadReadyForDeal
                                ? "is-ready"
                                : "is-locked"
                            }
                          >
                            <span>3</span>
                            <div>
                              <strong>
                                {t("deal", {
                                  defaultValue: "Сделка",
                                })}
                              </strong>
                              <small>
                                {selectedLeadReadyForDeal
                                  ? t("ready", {
                                      defaultValue: "Готово",
                                    })
                                  : t("locked", {
                                      defaultValue: "Недоступно",
                                    })}
                              </small>
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          data-academy="lead-convert"
                          className="pdLeadConvertButton"
                          disabled={!selectedLeadReadyForDeal || leadConverting}
                          onClick={openSelectedLeadForConversion}
                        >
                          <span>
                            <strong>
                              {leadConverting
                                ? t("converting_to_deal", {
                                    defaultValue: "Создание сделки...",
                                  })
                                : t("convert_to_deal", {
                                    defaultValue: "Конвертировать в сделку",
                                  })}
                            </strong>
                            <small>
                              {leadConverting
                                ? t("please_wait", {
                                    defaultValue: "Пожалуйста, подождите",
                                  })
                                : selectedLeadReadyForDeal
                                  ? t("continue_in_pipeline", {
                                      defaultValue:
                                        "Создать сделку и открыть её рабочую область",
                                    })
                                  : t("complete_required_steps", {
                                      defaultValue:
                                        "Сначала создайте задачу, затем сохраните бюджет",
                                    })}
                            </small>
                          </span>
                          <b>{leadConverting ? "…" : "→"}</b>
                        </button>
                      </section>
                    </div>

                    <section className="pdWorkSessionPanel pdLeadTimelinePanel">
                      <div className="pdWorkTimelineHeader">
                        <div>
                          <div className="pdWorkSessionPanelTitle">
                            {t("lead_timeline", {
                              defaultValue: "Таймлайн лида",
                            })}
                          </div>
                          <p>
                            {t("lead_timeline_hint", {
                              defaultValue:
                                "Только задачи и активности, связанные с выбранным лидом.",
                            })}
                          </p>
                        </div>
                        <span className="pdLeadTimelineCount">
                          {selectedLeadTimelineItems.length}
                        </span>
                      </div>

                      {selectedLeadTimelineItems.length === 0 ? (
                        <div className="pdWorkTimelineEmpty">
                          <span className="pdWorkTimelineEmptyIcon">◎</span>
                          <strong>
                            {t("timeline_empty", {
                              defaultValue: "Событий пока нет",
                            })}
                          </strong>
                        </div>
                      ) : (
                        <div className="pdLeadTimelineList">
                          {selectedLeadTimelineItems.map((item) => (
                            <article
                              key={item.key}
                              className={`pdLeadTimelineItem pdLeadTimelineItem--${item.kind}`}
                            >
                              <span className="pdLeadTimelineIcon">
                                {item.icon}
                              </span>
                              <div>
                                <div>
                                  <strong>{item.title}</strong>
                                  <time>{formatDate(item.date)}</time>
                                </div>
                                {item.description && (
                                  <p>{compactText(item.description, 180)}</p>
                                )}
                                <small>
                                  {item.status
                                    ? item.kind === "lead"
                                      ? getLeadStatusLabel(t, item.status)
                                      : t(item.status, {
                                          defaultValue: item.status,
                                        })
                                    : "--"}
                                </small>
                              </div>
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                  </section>
                </div>
              )}

              {selectedWork && (
                <div className="pdWorkSessionLayout">
                  <section className="pdWorkSessionMain">
                    <div className="pdWorkSessionHeading">
                      <div>
                        <span className="pdWorkSessionEyebrow">
                          {t("work_session", {
                            defaultValue: "Рабочая сессия",
                          })}{" "}
                          · #{selectedWork.ui_id ?? selectedWork.id}
                        </span>
                        <h5>{selectedWork.title || "--"}</h5>
                        <small>
                          {formatDateShort(selectedWork.created_at)} ·{" "}
                          {selectedWorkActivities.length}{" "}
                          {t("events", { defaultValue: "событий" })}
                        </small>
                      </div>

                      <div className="pdWorkSessionHeaderActions">
                        <button
                          type="button"
                          className={`pdAiLauncher ${
                            workAiData ? "has-result" : ""
                          } ${workAiPanelOpen ? "is-open" : ""}`}
                          onClick={() =>
                            setWorkAiPanelOpen((current) => !current)
                          }
                          title={t("work_ai_title", {
                            defaultValue: "AI-анализ выбранной работы",
                          })}
                          aria-label={t("work_ai_title", {
                            defaultValue: "AI-анализ выбранной работы",
                          })}
                          aria-expanded={workAiPanelOpen}
                        >
                          <span aria-hidden="true">✦</span>
                          {workAiData && (
                            <i
                              className="pdAiLauncherStatus"
                              aria-hidden="true"
                            />
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openContactDeal(selectedWork)}
                        >
                          {t("open_full", {
                            defaultValue: "Открыть полностью",
                          })}
                        </button>
                      </div>
                    </div>

                    {(selectedWorkLead || selectedWorkOriginTask) && (
                      <section className="pdDealOriginCard">
                        <div className="pdDealOriginHeader">
                          <div>
                            <span className="pdDealOriginEyebrow">
                              {t("deal_origin", {
                                defaultValue: "Основание сделки",
                              })}
                            </span>
                            <strong>
                              {t("deal_created_from_lead_context", {
                                defaultValue:
                                  "Исходный лид и задача сохранены как рабочий контекст.",
                              })}
                            </strong>
                          </div>

                          {selectedWork?.lead_id && (
                            <span className="pdDealOriginChain">
                              {t("lead", { defaultValue: "Лид" })} #
                              {selectedWorkLead?.ui_id ||
                                selectedWorkLead?.id ||
                                selectedWork.lead_id}
                              <i aria-hidden="true">→</i>
                              {t("deal", { defaultValue: "Сделка" })} #
                              {selectedWork.ui_id || selectedWork.id}
                            </span>
                          )}
                        </div>

                        <div className="pdDealOriginGrid">
                          {selectedWorkLead && (
                            <button
                              type="button"
                              className="pdDealOriginItem"
                              onClick={() => openContactLead(selectedWorkLead)}
                            >
                              <span className="pdDealOriginIcon pdDealOriginIcon--lead">
                                L
                              </span>
                              <span className="pdDealOriginContent">
                                <small>
                                  {t("source_lead", {
                                    defaultValue: "Исходный лид",
                                  })}
                                </small>
                                <strong>
                                  {selectedWorkLead.title ||
                                    `${t("lead", {
                                      defaultValue: "Лид",
                                    })} #${
                                      selectedWorkLead.ui_id ||
                                      selectedWorkLead.id
                                    }`}
                                </strong>
                                <em>
                                  {t(selectedWorkLead.status || "new", {
                                    defaultValue:
                                      selectedWorkLead.status || "new",
                                  })}
                                </em>
                              </span>
                              <span className="pdDealOriginArrow">→</span>
                            </button>
                          )}

                          {selectedWorkOriginTask && (
                            <button
                              type="button"
                              className="pdDealOriginItem"
                              onClick={() =>
                                openContactTask(selectedWorkOriginTask)
                              }
                            >
                              <span className="pdDealOriginIcon pdDealOriginIcon--task">
                                T
                              </span>
                              <span className="pdDealOriginContent">
                                <small>
                                  {t("source_task", {
                                    defaultValue: "Исходная задача",
                                  })}
                                </small>
                                <strong>
                                  {selectedWorkOriginTask.title ||
                                    `${t("task", {
                                      defaultValue: "Задача",
                                    })} #${
                                      selectedWorkOriginTask.ui_id ||
                                      selectedWorkOriginTask.id
                                    }`}
                                </strong>
                                <em>
                                  {selectedWorkOriginTask.description
                                    ? compactText(
                                        selectedWorkOriginTask.description,
                                        88,
                                      )
                                    : formatDate(
                                        selectedWorkOriginTask.due_at ||
                                          selectedWorkOriginTask.created_at,
                                      )}
                                </em>
                              </span>
                              <span className="pdDealOriginArrow">→</span>
                            </button>
                          )}
                        </div>

                        {selectedWorkOriginTasks.length > 1 && (
                          <div className="pdDealOriginMore">
                            +{selectedWorkOriginTasks.length - 1}{" "}
                            {t("more_linked_tasks", {
                              defaultValue: "связанных задач",
                            })}
                          </div>
                        )}
                      </section>
                    )}

                    <div className="pdWorkProgressBlock">
                      <div className="pdWorkProgressTop">
                        <span>
                          {t("work_progress", {
                            defaultValue: "Прогресс работы",
                          })}
                        </span>
                        <b>{selectedWorkProgress}%</b>
                      </div>
                      <div
                        className="pdWorkProgressTrack"
                        role="progressbar"
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={selectedWorkProgress}
                      >
                        <span style={{ width: `${selectedWorkProgress}%` }} />
                      </div>
                      <small>
                        {selectedWorkCompletedTasks}/{selectedWorkTasks.length}{" "}
                        {t("tasks_completed", {
                          defaultValue: "задач выполнено",
                        })}
                      </small>
                    </div>

                    <div className="pdWorkSessionColumns">
                      <div className="pdWorkSessionPanel pdWorkFinancePanel">
                        <div className="pdWorkFinanceHeader">
                          <div>
                            <div className="pdWorkSessionPanelTitle">
                              {t("payment", { defaultValue: "Финансы" })}
                            </div>
                            <span
                              className={`pdPaymentState pdPaymentState--${selectedWorkPaymentStatus}`}
                            >
                              {selectedWorkPaymentStatus === "paid"
                                ? t("paid_in_full", {
                                    defaultValue: "Оплачено полностью",
                                  })
                                : selectedWorkPaymentStatus === "partial"
                                  ? t("partially_paid", {
                                      defaultValue: "Частичная оплата",
                                    })
                                  : selectedWorkPaymentStatus === "unpaid"
                                    ? t("not_paid", {
                                        defaultValue: "Не оплачено",
                                      })
                                    : t("amount_not_set", {
                                        defaultValue: "Стоимость не указана",
                                      })}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="pdAddPaymentButton"
                            onClick={openPaymentModal}
                          >
                            +{" "}
                            {t("add_payment", {
                              defaultValue: "Добавить оплату",
                            })}
                          </button>
                        </div>

                        <div className="pdPaymentHero">
                          <div>
                            <span>
                              {t("amount", { defaultValue: "Стоимость" })}
                            </span>
                            <b>{selectedWorkAmount.toFixed(2)} €</b>
                          </div>
                          <div>
                            <span>
                              {t("paid", { defaultValue: "Оплачено" })}
                            </span>
                            <b>{selectedWorkPaid.toFixed(2)} €</b>
                          </div>
                          <div
                            className={
                              selectedWorkBalance > 0 ? "is-due" : "is-paid"
                            }
                          >
                            <span>
                              {t("balance", { defaultValue: "Осталось" })}
                            </span>
                            <b>{selectedWorkBalance.toFixed(2)} €</b>
                          </div>
                        </div>

                        <div
                          className="pdPaymentTrack"
                          role="progressbar"
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-valuenow={selectedWorkPaymentProgress}
                        >
                          <span
                            style={{ width: `${selectedWorkPaymentProgress}%` }}
                          />
                        </div>
                        <small className="pdPaymentCaption">
                          {selectedWorkPaymentProgress}%{" "}
                          {t("paid", { defaultValue: "оплачено" })}
                        </small>

                        <div className="pdPaymentHistory">
                          <div className="pdPaymentHistoryTitle">
                            <span>
                              {t("payment_history", {
                                defaultValue: "История оплат",
                              })}
                            </span>
                            <b>{selectedWorkPaymentHistory.length}</b>
                          </div>
                          {selectedWorkPaymentHistory.length === 0 ? (
                            <div className="pdPaymentHistoryEmpty">
                              {t("payment_history_empty", {
                                defaultValue:
                                  "Платежей по этой работе пока нет.",
                              })}
                            </div>
                          ) : (
                            <div className="pdPaymentHistoryList">
                              {selectedWorkPaymentHistory
                                .slice(0, 5)
                                .map((payment) => (
                                  <article key={payment.id}>
                                    <span className="pdPaymentHistoryIcon">
                                      €
                                    </span>
                                    <div>
                                      <b>
                                        {payment.amount != null
                                          ? `${payment.amount.toFixed(2)} €`
                                          : t("payment", {
                                              defaultValue: "Оплата",
                                            })}
                                      </b>
                                      <small>
                                        {payment.method
                                          ? t(payment.method, {
                                              defaultValue: payment.method,
                                            })
                                          : ""}
                                        {payment.note
                                          ? ` · ${payment.note}`
                                          : ""}
                                      </small>
                                    </div>
                                    <time>{formatDate(payment.date)}</time>
                                  </article>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="pdWorkSessionPanel">
                        <div className="pdWorkSessionPanelTitle">
                          {t("next_action", {
                            defaultValue: "Следующее действие",
                          })}
                        </div>

                        {selectedWorkNextTask ? (
                          <button
                            type="button"
                            className="pdWorkNextTask"
                            onClick={() =>
                              openContactTask(selectedWorkNextTask)
                            }
                          >
                            <span>
                              <b>{selectedWorkNextTask.title}</b>
                              <small>
                                {getTaskShortDescription(selectedWorkNextTask)}
                              </small>
                            </span>
                            <time>
                              {formatDate(selectedWorkNextTask.due_at)}
                            </time>
                          </button>
                        ) : (
                          <div className="pdWorkSessionEmpty">
                            {t("no_next_action", {
                              defaultValue:
                                "Нет открытых задач по этой работе.",
                            })}
                          </div>
                        )}

                        <button
                          type="button"
                          className="pdWorkAddTask"
                          onClick={createTaskForContact}
                        >
                          +{" "}
                          {t("contact_new_task", {
                            defaultValue: "Новая задача",
                          })}
                        </button>
                      </div>
                    </div>

                    {workAiPanelOpen && (
                      <div className="pdAiCompactPanelWrap">
                        <div className="pdWorkSessionPanel pdWorkAiPanel pdWorkAiPanel--compact">
                          <div className="pdWorkAiHeader">
                            <div>
                              <span className="pdWorkAiEyebrow">
                                {t("personal_ai_work_assistant")}
                              </span>
                              <div className="pdWorkSessionPanelTitle">
                                {t("work_ai_title", {
                                  defaultValue: "Умный анализ выбранной работы",
                                })}
                              </div>
                              <p>
                                {t("work_ai_hint", {
                                  defaultValue:
                                    "AI использует только задачи, активности, финансы и итог выбранной сделки.",
                                })}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="pdWorkAiGenerate"
                              disabled={workAiLoading}
                              onClick={generateWorkAi}
                            >
                              {workAiLoading
                                ? t("analyzing", { defaultValue: "Анализ..." })
                                : workAiData
                                  ? t("regenerate", {
                                      defaultValue: "Обновить",
                                    })
                                  : t("generate", {
                                      defaultValue: "Создать анализ",
                                    })}
                            </button>
                          </div>

                          {workAiError && (
                            <div className="pdWorkAiError">
                              {String(workAiError)}
                            </div>
                          )}

                          {!workAiData && !workAiLoading && !workAiError && (
                            <div className="pdWorkAiEmpty">
                              <span>✦</span>
                              <strong>
                                {t("work_ai_empty", {
                                  defaultValue: "Анализ ещё не создан",
                                })}
                              </strong>
                              <small>
                                {t("work_ai_empty_hint", {
                                  defaultValue:
                                    "Нажмите кнопку — система подготовит резюме, риски, следующий шаг и сообщение клиенту.",
                                })}
                              </small>
                            </div>
                          )}

                          {workAiLoading && (
                            <div
                              className="pdWorkAiSkeleton"
                              aria-label={t("loading", {
                                defaultValue: "Загрузка",
                              })}
                            >
                              <span />
                              <span />
                              <span />
                              <span />
                            </div>
                          )}

                          {workAiData && !workAiLoading && (
                            <div className="pdWorkAiContent">
                              <div className="pdWorkAiSummary">
                                <div className="pdWorkAiSummaryTop">
                                  <span>
                                    {t("summary", { defaultValue: "Резюме" })}
                                  </span>
                                  <b
                                    className={`pdWorkAiRisk pdWorkAiRisk--${workAiData.risk_level || "medium"}`}
                                  >
                                    {workAiData.risk_level === "low"
                                      ? t("risk_low", {
                                          defaultValue: "Низкий риск",
                                        })
                                      : workAiData.risk_level === "high"
                                        ? t("risk_high", {
                                            defaultValue: "Высокий риск",
                                          })
                                        : t("risk_medium", {
                                            defaultValue: "Средний риск",
                                          })}
                                  </b>
                                </div>
                                <p>{workAiData.summary}</p>
                                <small>
                                  {workAiData.provider === "openai"
                                    ? "OpenAI"
                                    : t("basic_ai", {
                                        defaultValue: "Базовый анализ",
                                      })}
                                </small>
                              </div>

                              <div className="pdWorkAiGrid">
                                <section>
                                  <h6>
                                    {t("highlights", {
                                      defaultValue: "Ключевые факты",
                                    })}
                                  </h6>
                                  <ul>
                                    {(workAiData.highlights || []).map(
                                      (item, index) => (
                                        <li key={`highlight-${index}`}>
                                          {item}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </section>
                                <section className="pdWorkAiRisks">
                                  <h6>
                                    {t("risks", { defaultValue: "Риски" })}
                                  </h6>
                                  {(workAiData.risks || []).length ? (
                                    <ul>
                                      {workAiData.risks.map((item, index) => (
                                        <li key={`risk-${index}`}>{item}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p>
                                      {t("no_risks_found", {
                                        defaultValue:
                                          "Критических рисков не обнаружено.",
                                      })}
                                    </p>
                                  )}
                                </section>
                              </div>

                              <div className="pdWorkAiNextStep">
                                <span>→</span>
                                <div>
                                  <small>
                                    {t("next_step", {
                                      defaultValue: "Следующий шаг",
                                    })}
                                  </small>
                                  <b>{workAiData.next_step}</b>
                                </div>
                              </div>

                              <div className="pdWorkAiOutputs">
                                <article>
                                  <div>
                                    <span>
                                      {t("completion_summary", {
                                        defaultValue: "Итог выполненных работ",
                                      })}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyWorkAiText(
                                          "summary",
                                          workAiData.completion_summary,
                                        )
                                      }
                                    >
                                      {workAiCopied === "summary"
                                        ? "✓"
                                        : t("copy", {
                                            defaultValue: "Копировать",
                                          })}
                                    </button>
                                  </div>
                                  <p>{workAiData.completion_summary}</p>
                                  {String(
                                    selectedWork.status || "",
                                  ).toLowerCase() === "open" && (
                                    <button
                                      type="button"
                                      className="pdWorkAiUseButton"
                                      onClick={useAiCompletionSummary}
                                    >
                                      {t("use_for_completion", {
                                        defaultValue:
                                          "Использовать при завершении",
                                      })}
                                    </button>
                                  )}
                                </article>
                                <article>
                                  <div>
                                    <span>
                                      {t("client_message", {
                                        defaultValue: "Сообщение клиенту",
                                      })}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        copyWorkAiText(
                                          "message",
                                          workAiData.client_message,
                                        )
                                      }
                                    >
                                      {workAiCopied === "message"
                                        ? "✓"
                                        : t("copy", {
                                            defaultValue: "Копировать",
                                          })}
                                    </button>
                                  </div>
                                  <p>{workAiData.client_message}</p>
                                  {primaryEmail && (
                                    <a
                                      className="pdWorkAiUseButton"
                                      href={`mailto:${primaryEmail}?body=${encodeURIComponent(workAiData.client_message || "")}`}
                                    >
                                      {t("send_email", {
                                        defaultValue: "Открыть письмо",
                                      })}
                                    </a>
                                  )}
                                </article>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div
                      className="pdWorkSessionPanel pdWorkTimelinePanel"
                      data-academy="work-timeline"
                    >
                      <div className="pdWorkTimelineHeader">
                        <div>
                          <div className="pdWorkSessionPanelTitle">
                            {t("work_timeline", {
                              defaultValue: "Таймлайн работы",
                            })}
                          </div>
                          <p>
                            {t("work_timeline_hint", {
                              defaultValue:
                                "Только задачи, заметки, звонки, сообщения и файлы выбранной работы.",
                            })}
                          </p>
                        </div>

                        <div className="pdWorkTimelineHeaderActions">
                          <button type="button" onClick={createTaskForContact}>
                            +{" "}
                            {t("contact_new_task", { defaultValue: "Задача" })}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/menu/sales/activities?contact_id=${contactId}&deal_id=${selectedWork.id}&create=1`,
                              )
                            }
                          >
                            + {t("note", { defaultValue: "Заметка" })}
                          </button>
                        </div>
                      </div>

                      <div className="pdWorkTimelineFilters">
                        {[
                          {
                            key: "all",
                            label: t("all", { defaultValue: "Все" }),
                          },
                          {
                            key: "task",
                            label: t("sales_tasks", { defaultValue: "Задачи" }),
                          },
                          {
                            key: "activity",
                            label: t("sales_activities", {
                              defaultValue: "Активности",
                            }),
                          },
                          {
                            key: "note",
                            label: t("notes", { defaultValue: "Заметки" }),
                          },
                          {
                            key: "call",
                            label: t("calls", { defaultValue: "Звонки" }),
                          },
                          {
                            key: "message",
                            label: t("messages", { defaultValue: "Сообщения" }),
                          },
                          {
                            key: "file",
                            label: t("files", { defaultValue: "Файлы" }),
                          },
                        ].map((filter) => (
                          <button
                            key={filter.key}
                            type="button"
                            className={
                              workTimelineFilter === filter.key
                                ? "is-active"
                                : ""
                            }
                            onClick={() => setWorkTimelineFilter(filter.key)}
                          >
                            {filter.label}
                          </button>
                        ))}
                        <span>
                          {filteredWorkTimelineItems.length}{" "}
                          {t("events", { defaultValue: "событий" })}
                        </span>
                      </div>

                      {filteredWorkTimelineItems.length === 0 ? (
                        <div className="pdWorkTimelineEmpty">
                          <span className="pdWorkTimelineEmptyIcon">◎</span>
                          <strong>
                            {t("timeline_empty", {
                              defaultValue: "Событий пока нет",
                            })}
                          </strong>
                          <small>
                            {t("work_timeline_empty_hint", {
                              defaultValue:
                                "Создайте задачу или добавьте заметку — она появится только в этой работе.",
                            })}
                          </small>
                          <button type="button" onClick={createTaskForContact}>
                            +{" "}
                            {t("create_first_task", {
                              defaultValue: "Создать первую задачу",
                            })}
                          </button>
                        </div>
                      ) : (
                        <div className="pdWorkTimelineGroups">
                          {Object.entries(groupedWorkTimelineItems).map(
                            ([date, items]) => (
                              <section key={date} className="pdWorkTimelineDay">
                                <div className="pdWorkTimelineDate">
                                  <span>{date}</span>
                                  <b>{items.length}</b>
                                </div>

                                <div className="pdWorkTimelineDayEvents">
                                  {items.map((item) => {
                                    const expanded = Boolean(
                                      expandedWorkTimelineItems[item.key],
                                    );
                                    const hasLongText =
                                      String(item.description || "").length >
                                      140;
                                    const hasDetails =
                                      hasLongText || item.files.length > 0;
                                    return (
                                      <article
                                        key={item.key}
                                        className={`pdWorkTimelineEvent pdWorkTimelineEvent--${item.kind} ${expanded ? "is-expanded" : ""}`}
                                      >
                                        <div className="pdWorkTimelineRail">
                                          <span>{item.icon}</span>
                                        </div>

                                        <div className="pdWorkTimelineEventBody">
                                          <div className="pdWorkTimelineEventTop">
                                            <div className="pdWorkTimelineEventIdentity">
                                              <span>
                                                {item.kind === "task"
                                                  ? t("sales_tasks", {
                                                      defaultValue: "Задача",
                                                    })
                                                  : item.kind === "activity"
                                                    ? t("sales_activities", {
                                                        defaultValue:
                                                          "Активность",
                                                      })
                                                    : t("system_event", {
                                                        defaultValue:
                                                          "Системное событие",
                                                      })}
                                              </span>
                                              <h6>{item.title}</h6>
                                            </div>
                                            <time dateTime={item.date}>
                                              {new Date(
                                                item.date,
                                              ).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                              })}
                                            </time>
                                          </div>

                                          {item.description && (
                                            <p>
                                              {expanded
                                                ? item.description
                                                : compactText(
                                                    item.description,
                                                    140,
                                                  )}
                                            </p>
                                          )}

                                          {expanded &&
                                            item.files.length > 0 && (
                                              <div className="pdWorkTimelineAttachments">
                                                {item.files.map((file) => {
                                                  const image = String(
                                                    file.mime_type || "",
                                                  ).startsWith("image/");
                                                  const url =
                                                    file.web_view_link ||
                                                    file.web_content_link ||
                                                    file.thumbnail_link ||
                                                    "#";
                                                  return (
                                                    <a
                                                      key={file.id}
                                                      href={url}
                                                      target="_blank"
                                                      rel="noreferrer"
                                                      title={
                                                        file.file_name || "file"
                                                      }
                                                    >
                                                      {image &&
                                                      file.thumbnail_link ? (
                                                        <img
                                                          src={
                                                            file.thumbnail_link
                                                          }
                                                          alt={
                                                            file.file_name ||
                                                            "file"
                                                          }
                                                        />
                                                      ) : (
                                                        <span>
                                                          {image ? "🖼" : "📄"}
                                                        </span>
                                                      )}
                                                      <small>
                                                        {file.file_name ||
                                                          t("file", {
                                                            defaultValue:
                                                              "Файл",
                                                          })}
                                                      </small>
                                                    </a>
                                                  );
                                                })}
                                              </div>
                                            )}

                                          <div className="pdWorkTimelineEventFooter">
                                            <span
                                              className={`pdStatusBadge ${statusClass(item.status)}`}
                                            >
                                              {item.status
                                                ? t(item.status, {
                                                    defaultValue: item.status,
                                                  })
                                                : "--"}
                                            </span>
                                            {item.files.length > 0 && (
                                              <span>
                                                📎 {item.files.length}
                                              </span>
                                            )}
                                            <span>
                                              {formatDateShort(item.date)}
                                            </span>
                                            <div>
                                              {hasDetails && (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    toggleWorkTimelineItem(
                                                      item.key,
                                                    )
                                                  }
                                                >
                                                  {expanded
                                                    ? t("collapse", {
                                                        defaultValue:
                                                          "Свернуть",
                                                      })
                                                    : t("details", {
                                                        defaultValue:
                                                          "Подробнее",
                                                      })}
                                                </button>
                                              )}
                                              <button
                                                type="button"
                                                data-academy="work-timeline-open"
                                                data-academy-entity-title={
                                                  item.title || ""
                                                }
                                                onClick={() =>
                                                  openWorkTimelineItem(item)
                                                }
                                              >
                                                {t("open", {
                                                  defaultValue: "Открыть",
                                                })}{" "}
                                                →
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </article>
                                    );
                                  })}
                                </div>
                              </section>
                            ),
                          )}
                        </div>
                      )}
                    </div>

                    <div className="pdWorkSessionPanel pdWorkGalleryPanel">
                      <input
                        ref={workPhotoInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(event) =>
                          uploadWorkPhotos(
                            event.target.files,
                            filesWorkspaceLayout === "workflow"
                              ? workPhotoUploadCategory
                              : "during",
                          )
                        }
                      />

                      <div className="pdWorkGalleryHeader">
                        <div>
                          <div className="pdWorkSessionPanelTitle">
                            {t("work_photos", {
                              defaultValue: "Фотографии работы",
                            })}
                          </div>
                          <p>
                            {filesWorkspaceLayout === "workflow"
                              ? t("work_photos_hint", {
                                  defaultValue:
                                    "Фиксируйте исходное состояние, процесс и готовый результат.",
                                })
                              : t("work_photos_gallery_hint", {
                                  defaultValue:
                                    "Все фотографии выбранной работы в одной компактной галерее.",
                                })}
                          </p>
                        </div>

                        <div className="pdWorkGalleryHeaderActions">
                          {filesWorkspaceLayout === "workflow" &&
                            selectedWorkFileGroups.before.some((file) =>
                              String(file.mime_type || "").startsWith("image/"),
                            ) &&
                            selectedWorkFileGroups.after.some((file) =>
                              String(file.mime_type || "").startsWith("image/"),
                            ) && (
                              <button
                                type="button"
                                className="pdWorkCompareBtn"
                                onClick={() => setWorkPhotoCompareOpen(true)}
                              >
                                ◫{" "}
                                {t("compare_before_after", {
                                  defaultValue: "Сравнить До / После",
                                })}
                              </button>
                            )}

                          <button
                            type="button"
                            data-academy="work-add-photo"
                            className="pdWorkAddPhotoBtn"
                            onClick={() => openWorkPhotoPicker("during")}
                            disabled={workPhotoUploading}
                          >
                            ＋{" "}
                            {workPhotoUploading
                              ? t("uploading", {
                                  defaultValue: "Загрузка...",
                                })
                              : t("add_photos", {
                                  defaultValue: "Добавить фото",
                                })}
                          </button>
                        </div>
                      </div>

                      {workPhotoError && (
                        <div className="pdWorkGalleryError">
                          {workPhotoError}
                        </div>
                      )}

                      <div
                        className={`pdWorkPhotoDropZone ${
                          workPhotoDragActive ? "is-dragging" : ""
                        }`}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          setWorkPhotoDragActive(true);
                        }}
                        onDragOver={(event) => event.preventDefault()}
                        onDragLeave={() => setWorkPhotoDragActive(false)}
                        onDrop={(event) => handleWorkPhotoDrop(event, "during")}
                        onClick={() => openWorkPhotoPicker("during")}
                        role="button"
                        tabIndex={0}
                      >
                        <span>⇧</span>
                        <div>
                          <b>
                            {t("drop_photos_here", {
                              defaultValue: "Перетащите фотографии сюда",
                            })}
                          </b>
                          <small>
                            {t("or_select_files", {
                              defaultValue: "или нажмите, чтобы выбрать файлы",
                            })}
                          </small>
                        </div>
                      </div>

                      {filesWorkspaceLayout === "workflow" ? (
                        <div className="pdWorkGalleryColumns">
                          {[
                            {
                              key: "before",
                              label: t("before_work", {
                                defaultValue: "До работы",
                              }),
                              hint: t("before_work_hint", {
                                defaultValue: "Исходное состояние",
                              }),
                            },
                            {
                              key: "during",
                              label: t("during_work", {
                                defaultValue: "В процессе",
                              }),
                              hint: t("during_work_hint", {
                                defaultValue: "Ход выполнения",
                              }),
                            },
                            {
                              key: "after",
                              label: t("after_work", {
                                defaultValue: "После работы",
                              }),
                              hint: t("after_work_hint", {
                                defaultValue: "Готовый результат",
                              }),
                            },
                          ].map((group) => {
                            const photos = selectedWorkFileGroups[
                              group.key
                            ].filter((file) =>
                              String(file.mime_type || "").startsWith("image/"),
                            );

                            return (
                              <section
                                key={group.key}
                                className={`pdWorkGalleryColumn pdWorkGalleryColumn--${group.key}`}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) =>
                                  handleWorkPhotoDrop(event, group.key)
                                }
                              >
                                <header>
                                  <div>
                                    <span>{group.label}</span>
                                    <small>{group.hint}</small>
                                  </div>
                                  <b>{photos.length}</b>
                                </header>

                                <div className="pdWorkGalleryGrid">
                                  {photos.map((file, index) => (
                                    <article
                                      data-academy="work-photo-item"
                                      key={file.id || `${group.key}-${index}`}
                                      className="pdWorkGalleryCard"
                                    >
                                      <button
                                        type="button"
                                        className="pdWorkGalleryImage"
                                        onClick={() =>
                                          setWorkPhotoLightbox({
                                            ...file,
                                            group: group.key,
                                          })
                                        }
                                      >
                                        <img
                                          src={
                                            file.thumbnail_link ||
                                            getWorkFileUrl(file)
                                          }
                                          alt={file.file_name || group.label}
                                        />
                                        <span>⌕</span>
                                      </button>

                                      <div>
                                        <span title={file.file_name || ""}>
                                          {String(
                                            file.file_name || "photo",
                                          ).replace(
                                            /^((before|during|after)__)+/i,
                                            "",
                                          )}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => deleteWorkPhoto(file)}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    </article>
                                  ))}

                                  <button
                                    type="button"
                                    className="pdWorkGalleryAddCard"
                                    onClick={() =>
                                      openWorkPhotoPicker(group.key)
                                    }
                                    disabled={workPhotoUploading}
                                  >
                                    <span>＋</span>
                                    <small>
                                      {t("add", {
                                        defaultValue: "Добавить",
                                      })}
                                    </small>
                                  </button>
                                </div>
                              </section>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="pdWorkUnifiedGallery">
                          {selectedWorkFiles
                            .filter((file) =>
                              String(file.mime_type || "").startsWith("image/"),
                            )
                            .map((file, index) => (
                              <article
                                data-academy="work-photo-item"
                                key={file.id || `gallery-${index}`}
                                className="pdWorkUnifiedGalleryCard"
                              >
                                <button
                                  type="button"
                                  className="pdWorkUnifiedGalleryImage"
                                  onClick={() =>
                                    setWorkPhotoLightbox({
                                      ...file,
                                      group: classifyWorkFile(file),
                                    })
                                  }
                                >
                                  <img
                                    src={
                                      file.thumbnail_link ||
                                      getWorkFileUrl(file)
                                    }
                                    alt={file.file_name || "photo"}
                                  />
                                  <span>⌕</span>
                                </button>

                                <div className="pdWorkUnifiedGalleryMeta">
                                  <span title={file.file_name || ""}>
                                    {String(file.file_name || "photo").replace(
                                      /^((before|during|after)__)+/i,
                                      "",
                                    )}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => deleteWorkPhoto(file)}
                                    title={t("delete", {
                                      defaultValue: "Удалить",
                                    })}
                                  >
                                    ×
                                  </button>
                                </div>
                              </article>
                            ))}

                          <button
                            type="button"
                            className="pdWorkUnifiedGalleryAdd"
                            onClick={() => openWorkPhotoPicker("during")}
                            disabled={workPhotoUploading}
                          >
                            <span>＋</span>
                            <strong>
                              {t("add_photos", {
                                defaultValue: "Добавить фото",
                              })}
                            </strong>
                          </button>
                        </div>
                      )}
                    </div>
                  </section>

                  <aside className="pdWorkSessionAside">
                    <div className="pdWorkAsideCard">
                      <span>
                        {t("current_work", { defaultValue: "Текущая работа" })}
                      </span>
                      <b>{selectedWork.title || "--"}</b>
                      <small>
                        #{selectedWork.ui_id ?? selectedWork.id} ·{" "}
                        {formatDateShort(selectedWork.created_at)}
                      </small>
                    </div>

                    <div className="pdWorkAsideCard">
                      <span>
                        {t("next_action", {
                          defaultValue: "Следующее действие",
                        })}
                      </span>
                      <b>{selectedWorkNextTask?.title || "—"}</b>
                      <small>{formatDate(selectedWorkNextTask?.due_at)}</small>
                    </div>

                    <div className="pdWorkAsideCard">
                      <span>{t("files", { defaultValue: "Файлы" })}</span>
                      <b>{selectedWorkFiles.length}</b>
                      <small>
                        {selectedWorkActivities.length}{" "}
                        {t("events", { defaultValue: "событий" })}
                      </small>
                    </div>

                    <button
                      type="button"
                      className="pdWorkCompleteButton"
                      onClick={openCompleteWork}
                    >
                      ✓{" "}
                      {t("complete_work", { defaultValue: "Завершить работу" })}
                    </button>

                    <button
                      type="button"
                      className="pdWorkAsidePrimary"
                      onClick={() => openContactDeal(selectedWork)}
                    >
                      {t("open_work", { defaultValue: "Открыть работу" })}
                    </button>
                  </aside>
                </div>
              )}

              <div className="pdWorksSection">
                <div className="pdWorksSectionTitle pdWorksSectionTitleWithFilters">
                  <div className="pdWorksTitleText">
                    <span>
                      {t("all_contact_work", {
                        defaultValue: "Лиды и сделки",
                      })}
                    </span>
                    <b>{activeWorkItemsCount}</b>
                  </div>

                  <div
                    className="pdWorkTypeFilters"
                    role="group"
                    aria-label={t("work_type_filter", {
                      defaultValue: "Фильтр типов работ",
                    })}
                  >
                    <label
                      className={`pdWorkTypeSwitch pdWorkTypeSwitch--lead ${
                        showLeadWorkItems ? "is-active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={showLeadWorkItems}
                        onChange={(event) =>
                          setShowLeadWorkItems(event.target.checked)
                        }
                      />
                      <span
                        className="pdWorkTypeSwitchDot"
                        aria-hidden="true"
                      />
                      <span>{t("leads", { defaultValue: "Лиды" })}</span>
                      <small>{activeLeadWorks.length}</small>
                    </label>

                    <label
                      className={`pdWorkTypeSwitch pdWorkTypeSwitch--deal ${
                        showDealWorkItems ? "is-active" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={showDealWorkItems}
                        onChange={(event) =>
                          setShowDealWorkItems(event.target.checked)
                        }
                      />
                      <span
                        className="pdWorkTypeSwitchDot"
                        aria-hidden="true"
                      />
                      <span>{t("deals", { defaultValue: "Сделки" })}</span>
                      <small>{activeWorks.length}</small>
                    </label>
                  </div>
                </div>

                {dealsLoading || leadsLoading ? (
                  <div className="pdWorksEmpty">{t("loading")}</div>
                ) : activeWorkItemsCount === 0 ? (
                  <div className="pdWorksEmpty">
                    <strong>
                      {!showLeadWorkItems && !showDealWorkItems
                        ? t("work_filters_disabled", {
                            defaultValue: "Оба типа скрыты",
                          })
                        : t("no_active_work", {
                            defaultValue: "Нет текущих лидов и сделок",
                          })}
                    </strong>
                    <span>
                      {!showLeadWorkItems && !showDealWorkItems
                        ? t("work_filters_enable_hint", {
                            defaultValue:
                              "Включите «Лиды» или «Сделки», чтобы показать элементы.",
                          })
                        : t("no_active_work_hint", {
                            defaultValue:
                              "Создайте новый лид, чтобы начать работу с клиентом.",
                          })}
                    </span>
                  </div>
                ) : (
                  <div className="pdWorksGrid">
                    {visibleLeadWorks.map((lead) => {
                      const leadTasks = getLeadWorkTasks(lead);
                      const completedTasks = leadTasks.filter(
                        (task) =>
                          String(task.status || "").toLowerCase() === "done",
                      ).length;
                      const isSelected =
                        Number(selectedLeadWork?.id) === Number(lead.id);

                      return (
                        <article
                          key={`lead-${lead.id}`}
                          className={`pdWorkCardV2 pdWorkCardV2Clickable pdEntityCard--lead ${
                            isSelected ? "is-selected" : ""
                          }`}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          onClick={() => {
                            setSelectedLeadWorkId(lead.id);
                            setSelectedWorkId(null);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedLeadWorkId(lead.id);
                              setSelectedWorkId(null);
                            }
                          }}
                        >
                          <div className="pdWorkCardV2Top">
                            <div>
                              <span className="pdEntityTypeBadge pdEntityTypeBadge--lead">
                                {t("lead", { defaultValue: "ЛИД" })}
                              </span>
                              <h6>{lead.title || "--"}</h6>
                            </div>

                            <span
                              className={`pdStatusBadge ${statusClass(
                                lead.status,
                              )}`}
                            >
                              {getLeadStatusLabel(t, lead.status)}
                            </span>
                          </div>

                          <div className="pdLeadCardMeta">
                            <span>
                              {t("lead_source", {
                                defaultValue: "Источник",
                              })}
                              :{" "}
                              <b>
                                {getLeadSourceLabel(
                                  t,
                                  lead.source_name || lead.source,
                                )}
                              </b>
                            </span>
                            <span>
                              {t("lead_budget", {
                                defaultValue: "Бюджет",
                              })}
                              :{" "}
                              <b>
                                {lead.budget != null
                                  ? `${Number(lead.budget).toFixed(2)} €`
                                  : "--"}
                              </b>
                            </span>
                          </div>

                          <div className="pdWorkFacts">
                            <span>
                              {completedTasks}/{leadTasks.length}{" "}
                              {t("sales_tasks", { defaultValue: "задач" })}
                            </span>
                            <span>
                              {t("lead", { defaultValue: "Лид" })} #
                              {lead.ui_id ?? lead.id}
                            </span>
                            <span>{formatDateShort(lead.created_at)}</span>
                          </div>

                          <div className="pdWorkCardActions">
                            <button
                              type="button"
                              className="pdWorkSelect"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedLeadWorkId(lead.id);
                                setSelectedWorkId(null);
                              }}
                            >
                              {isSelected
                                ? t("selected", {
                                    defaultValue: "Выбрано",
                                  })
                                : t("view_details", {
                                    defaultValue: "Показать данные",
                                  })}
                            </button>

                            <button
                              type="button"
                              className="pdWorkOpen"
                              onClick={(event) => {
                                event.stopPropagation();
                                openContactLead(lead);
                              }}
                            >
                              {t("open", { defaultValue: "Открыть" })} →
                            </button>
                          </div>
                        </article>
                      );
                    })}

                    {visibleDealWorks.map((deal) => {
                      const workTasks = getWorkTasks(deal);
                      const workFiles = getWorkFiles(deal);
                      const completedTasks = workTasks.filter(
                        (task) =>
                          String(task.status || "").toLowerCase() === "done",
                      ).length;
                      const isSelected =
                        Number(selectedWork?.id) === Number(deal.id);

                      return (
                        <article
                          key={`deal-${deal.id}`}
                          className={`pdWorkCardV2 pdWorkCardV2Clickable pdEntityCard--deal ${
                            isSelected ? "is-selected" : ""
                          }`}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          onClick={() => {
                            setSelectedWorkId(deal.id);
                            setSelectedLeadWorkId(null);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedWorkId(deal.id);
                              setSelectedLeadWorkId(null);
                            }
                          }}
                        >
                          <div className="pdWorkCardV2Top">
                            <div>
                              <span className="pdEntityTypeBadge pdEntityTypeBadge--deal">
                                {t("deal", { defaultValue: "СДЕЛКА" })}
                              </span>
                              <h6>{deal.title || "--"}</h6>
                            </div>

                            <span className="pdStatusBadge pdBadgeInfo">
                              {t("in_progress", {
                                defaultValue: "В процессе",
                              })}
                            </span>
                          </div>

                          {deal.lead_id && (
                            <div className="pdConvertedLeadNote">
                              {t("converted_from_lead", {
                                defaultValue: "Создана из лида",
                              })}{" "}
                              #{deal.lead_ui_id || deal.lead_id}
                            </div>
                          )}

                          <div className="pdWorkFinance">
                            <div>
                              <span>
                                {t("amount", { defaultValue: "Сумма" })}
                              </span>
                              <b>{Number(deal.amount || 0).toFixed(2)} €</b>
                            </div>
                            <div>
                              <span>
                                {t("paid", { defaultValue: "Оплачено" })}
                              </span>
                              <b>{Number(deal.prepayment || 0).toFixed(2)} €</b>
                            </div>
                            <div>
                              <span>
                                {t("balance", { defaultValue: "Остаток" })}
                              </span>
                              <b>{Number(deal.balance || 0).toFixed(2)} €</b>
                            </div>
                          </div>

                          <div className="pdWorkFacts">
                            <span>
                              {completedTasks}/{workTasks.length}{" "}
                              {t("sales_tasks", { defaultValue: "задач" })}
                            </span>
                            <span>
                              {workFiles.length}{" "}
                              {t("files", { defaultValue: "файлов" })}
                            </span>
                            <span>{formatDateShort(deal.created_at)}</span>
                          </div>

                          <div className="pdWorkCardActions">
                            <button
                              type="button"
                              className="pdWorkSelect"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedWorkId(deal.id);
                                setSelectedLeadWorkId(null);
                              }}
                            >
                              {isSelected
                                ? t("selected", {
                                    defaultValue: "Выбрано",
                                  })
                                : t("work_session", {
                                    defaultValue: "Рабочая область",
                                  })}
                            </button>

                            <button
                              type="button"
                              className="pdWorkOpen"
                              onClick={(event) => {
                                event.stopPropagation();
                                openContactDeal(deal);
                              }}
                            >
                              {t("open", { defaultValue: "Открыть" })} →
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="pdWorksSection">
              <div className="pdWorksSectionTitle pdArchiveSectionTitle">
                <div>
                  <span>
                    {t("work_archive", { defaultValue: "Архив работ" })}
                  </span>
                  <small>
                    {t("work_archive_hint", {
                      defaultValue:
                        "Завершённые сделки, результаты, суммы и фотографии",
                    })}
                  </small>
                </div>
                <b>
                  {filteredArchivedWorks.length}/{archivedWorks.length}
                </b>
              </div>

              {archivedWorks.length > 0 && (
                <>
                  <div className="pdArchiveStats">
                    <div>
                      <span>{t("completed", { defaultValue: "Успешно" })}</span>
                      <b>{archiveWonCount}</b>
                    </div>
                    <div>
                      <span>{t("lost", { defaultValue: "Отменено" })}</span>
                      <b>{archiveLostCount}</b>
                    </div>
                    <div>
                      <span>
                        {t("archive_revenue", { defaultValue: "Сумма архива" })}
                      </span>
                      <b>{archiveRevenue.toFixed(2)} €</b>
                    </div>
                  </div>
                  <div className="pdArchiveToolbar">
                    <label className="pdArchiveSearch">
                      <i className="lni lni-search-alt" />
                      <input
                        value={archiveSearch}
                        onChange={(event) =>
                          setArchiveSearch(event.target.value)
                        }
                        placeholder={t("archive_search", {
                          defaultValue: "Поиск по названию или итогу...",
                        })}
                      />
                    </label>
                    <select
                      value={archiveStatusFilter}
                      onChange={(event) =>
                        setArchiveStatusFilter(event.target.value)
                      }
                    >
                      <option value="all">
                        {t("all_statuses", { defaultValue: "Все статусы" })}
                      </option>
                      <option value="won">
                        {t("won", { defaultValue: "Успешно" })}
                      </option>
                      <option value="lost">
                        {t("lost", { defaultValue: "Отменено" })}
                      </option>
                    </select>
                    <select
                      value={archivePeriodFilter}
                      onChange={(event) =>
                        setArchivePeriodFilter(event.target.value)
                      }
                    >
                      <option value="all">
                        {t("all_time", { defaultValue: "За всё время" })}
                      </option>
                      <option value="30d">
                        {t("last_30_days", {
                          defaultValue: "Последние 30 дней",
                        })}
                      </option>
                      <option value="90d">
                        {t("last_90_days", {
                          defaultValue: "Последние 90 дней",
                        })}
                      </option>
                      <option value="year">
                        {t("current_year", { defaultValue: "Текущий год" })}
                      </option>
                    </select>
                    <select
                      value={archiveSort}
                      onChange={(event) => setArchiveSort(event.target.value)}
                    >
                      <option value="newest">
                        {t("newest_first", { defaultValue: "Сначала новые" })}
                      </option>
                      <option value="oldest">
                        {t("oldest_first", { defaultValue: "Сначала старые" })}
                      </option>
                      <option value="amount_desc">
                        {t("amount_high_first", {
                          defaultValue: "Сумма: по убыванию",
                        })}
                      </option>
                      <option value="amount_asc">
                        {t("amount_low_first", {
                          defaultValue: "Сумма: по возрастанию",
                        })}
                      </option>
                    </select>
                    <label className="pdArchiveFilesToggle">
                      <input
                        type="checkbox"
                        checked={archiveWithFilesOnly}
                        onChange={(event) =>
                          setArchiveWithFilesOnly(event.target.checked)
                        }
                      />
                      <span>
                        {t("with_files", { defaultValue: "С файлами" })}
                      </span>
                    </label>
                  </div>
                </>
              )}

              {archivedWorks.length === 0 ? (
                <div className="pdWorksEmpty">
                  <strong>
                    {t("archive_empty", { defaultValue: "Архив пока пуст" })}
                  </strong>
                  <span>
                    {t("archive_empty_hint", {
                      defaultValue:
                        "Выигранные и проигранные сделки появятся здесь автоматически.",
                    })}
                  </span>
                </div>
              ) : filteredArchivedWorks.length === 0 ? (
                <div className="pdWorksEmpty pdArchiveNoResults">
                  <strong>
                    {t("archive_no_results", {
                      defaultValue: "Ничего не найдено",
                    })}
                  </strong>
                  <span>
                    {t("archive_no_results_hint", {
                      defaultValue: "Измените поиск или сбросьте фильтры.",
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setArchiveSearch("");
                      setArchiveStatusFilter("all");
                      setArchivePeriodFilter("all");
                      setArchiveSort("newest");
                      setArchiveWithFilesOnly(false);
                    }}
                  >
                    {t("reset_filters", { defaultValue: "Сбросить фильтры" })}
                  </button>
                </div>
              ) : (
                <div className="pdWorkArchive">
                  {filteredArchivedWorks.map((deal) => {
                    const workTasks = getWorkTasks(deal);
                    const workActivities = getWorkActivities(deal);
                    const workFiles = getWorkFiles(deal);
                    const won =
                      String(deal.status || "").toLowerCase() === "won";

                    return (
                      <React.Fragment key={deal.id}>
                        <button
                          type="button"
                          className="pdWorkArchiveRow"
                          onClick={() =>
                            setSelectedArchiveWorkId((current) =>
                              Number(current) === Number(deal.id)
                                ? null
                                : deal.id,
                            )
                          }
                        >
                          <span
                            className={`pdWorkArchiveIcon ${
                              won ? "isWon" : "isLost"
                            }`}
                          >
                            {won ? "✓" : "×"}
                          </span>

                          <span className="pdWorkArchiveMain">
                            <b>
                              {deal.title ||
                                `${t("work", { defaultValue: "Работа" })} #${
                                  deal.ui_id ?? deal.id
                                }`}
                            </b>
                            <small>
                              {formatDateShort(
                                deal.completed_at || deal.created_at,
                              )}{" "}
                              · {workTasks.length}{" "}
                              {t("sales_tasks", { defaultValue: "задач" })} ·{" "}
                              {workActivities.length}{" "}
                              {t("sales_activities", {
                                defaultValue: "событий",
                              })}{" "}
                              · {workFiles.length}{" "}
                              {t("files", { defaultValue: "файлов" })}
                            </small>
                          </span>

                          <span className="pdWorkArchiveAmount">
                            {Number(deal.amount || 0).toFixed(2)} €
                          </span>

                          <span
                            className={`pdStatusBadge ${
                              won ? "pdBadgeSuccess" : "pdBadgeDanger"
                            }`}
                          >
                            {t(deal.status, { defaultValue: deal.status })}
                          </span>
                        </button>

                        {Number(selectedArchiveWork?.id) ===
                          Number(deal.id) && (
                          <div className="pdArchiveDetail">
                            <div className="pdArchiveDetailHeader">
                              <div>
                                <span>
                                  {t("work_result", {
                                    defaultValue: "Итог работы",
                                  })}
                                </span>
                                <b>{deal.title || "--"}</b>
                              </div>
                              <button
                                type="button"
                                onClick={() => openContactDeal(deal)}
                              >
                                {t("open_full", {
                                  defaultValue: "Открыть полностью",
                                })}{" "}
                                →
                              </button>
                            </div>
                            <p>
                              {deal.work_summary ||
                                t("work_summary_legacy", {
                                  defaultValue:
                                    "Работа была завершена до добавления итоговых отчётов.",
                                })}
                            </p>
                            <div className="pdArchiveDetailKpis">
                              <div>
                                <span>
                                  {t("sales_tasks", { defaultValue: "Задачи" })}
                                </span>
                                <b>
                                  {
                                    workTasks.filter(
                                      (task) =>
                                        String(
                                          task.status || "",
                                        ).toLowerCase() === "done",
                                    ).length
                                  }
                                  /{workTasks.length}
                                </b>
                              </div>
                              <div>
                                <span>
                                  {t("sales_activities", {
                                    defaultValue: "События",
                                  })}
                                </span>
                                <b>{workActivities.length}</b>
                              </div>
                              <div>
                                <span>
                                  {t("files", { defaultValue: "Файлы" })}
                                </span>
                                <b>{workFiles.length}</b>
                              </div>
                              <div>
                                <span>
                                  {t("paid", { defaultValue: "Оплачено" })}
                                </span>
                                <b>
                                  {Number(deal.prepayment || 0).toFixed(2)} €
                                </b>
                              </div>
                            </div>
                            <div className="pdArchiveMeta">
                              <span>
                                {t("completed_at", {
                                  defaultValue: "Завершено",
                                })}
                                :{" "}
                                {formatDate(
                                  deal.completed_at || deal.created_at,
                                )}
                              </span>
                              <span>
                                {workFiles.length}{" "}
                                {t("files", { defaultValue: "файлов" })}
                              </span>
                              <span>
                                {Number(deal.amount || 0).toFixed(2)} €
                              </span>
                            </div>
                            {workFiles.length > 0 && (
                              <div className="pdArchivePhotos">
                                {workFiles.slice(0, 8).map((file) => {
                                  const url =
                                    file.web_view_link ||
                                    file.web_content_link ||
                                    file.thumbnail_link ||
                                    "#";
                                  const isImage = String(
                                    file.mime_type || "",
                                  ).startsWith("image/");
                                  return (
                                    <a
                                      key={file.id}
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {isImage && file.thumbnail_link ? (
                                        <img
                                          src={file.thumbnail_link}
                                          alt={file.file_name || "work"}
                                        />
                                      ) : (
                                        <span>{isImage ? "🖼" : "📄"}</span>
                                      )}
                                    </a>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {topTab === "activities" && (
          <section
            data-academy="personal-timeline"
            className="pdTimelineWorkspace"
          >
            <div className="pdTimelineHeader">
              <div>
                <h5>
                  {t("contact_timeline", { defaultValue: "История клиента" })}
                </h5>
                <p>
                  {t("contact_timeline_hint", {
                    defaultValue:
                      "Все действия, лиды, сделки, задачи и файлы в одной хронологии.",
                  })}
                </p>
              </div>

              <div
                data-academy="personal-timeline-actions"
                className="pdTimelineHeaderActions"
              >
                <button
                  type="button"
                  data-academy="timeline-refresh"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={activitiesLoading}
                  onClick={() => setActivitiesLoaded(false)}
                >
                  {activitiesLoading
                    ? t("loading", { defaultValue: "Загрузка" }) + "..."
                    : t("refresh", { defaultValue: "Обновить Timeline" })}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={createTaskForContact}
                >
                  + {t("contact_new_task", { defaultValue: "Задача" })}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    navigate(
                      `/menu/sales/activities?contact_id=${contactId}&create=1`,
                    )
                  }
                >
                  + {t("contact_log_activity", { defaultValue: "Активность" })}
                </button>
              </div>
            </div>

            <div
              data-academy="personal-timeline-filters"
              className="pdTimelineFilterBar"
            >
              {[
                { key: "all", label: t("all", { defaultValue: "Все" }) },
                {
                  key: "activity",
                  label: t("sales_activities", {
                    defaultValue: "Активности",
                  }),
                },
                {
                  key: "task",
                  label: t("sales_tasks", { defaultValue: "Задачи" }),
                },
                {
                  key: "lead",
                  label: t("sales_leads", { defaultValue: "Лиды" }),
                },
                {
                  key: "deal",
                  label: t("sales_deals", { defaultValue: "Сделки" }),
                },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`pdTimelineFilter ${
                    timelineFilter === filter.key ? "is-active" : ""
                  }`}
                  onClick={() => setTimelineFilter(filter.key)}
                >
                  {filter.label}
                </button>
              ))}

              <span className="pdTimelineTotal">
                {filteredTimelineItems.length}{" "}
                {t("events", { defaultValue: "событий" })}
              </span>
            </div>

            {activitiesLoading ||
            tasksLoading ||
            leadsLoading ||
            dealsLoading ? (
              <div className="pdTimelineEmpty">{t("loading")}</div>
            ) : filteredTimelineItems.length === 0 ? (
              <div className="pdTimelineEmpty">
                <strong>
                  {t("contact_no_activities", {
                    defaultValue: "История пока пустая",
                  })}
                </strong>
                <span>
                  {t("contact_timeline_empty_hint", {
                    defaultValue:
                      "Создайте задачу, активность, лид или сделку — событие появится здесь.",
                  })}
                </span>
              </div>
            ) : (
              <div
                data-academy="personal-timeline-events"
                className="pdTimelineGroups"
              >
                {Object.entries(groupedTimelineItems).map(([date, items]) => (
                  <section key={date} className="pdTimelineDay">
                    <div className="pdTimelineDate">{date}</div>

                    <div className="pdTimelineDayItems">
                      {items.map((item) => (
                        <article
                          key={item.key}
                          data-academy="timeline-event"
                          data-academy-entity-title={item.title || ""}
                          className="pdTimelineEvent"
                        >
                          <div
                            className={`pdTimelineEventIcon pdTimelineEventIcon--${item.kind}`}
                            aria-hidden="true"
                          >
                            {item.icon}
                          </div>

                          <div className="pdTimelineEventContent">
                            <div className="pdTimelineEventTop">
                              <div>
                                <span className="pdTimelineEventKind">
                                  {item.kind === "activity"
                                    ? t("sales_activities", {
                                        defaultValue: "Активность",
                                      })
                                    : item.kind === "task"
                                      ? t("sales_tasks", {
                                          defaultValue: "Задача",
                                        })
                                      : item.kind === "lead"
                                        ? t("sales_leads", {
                                            defaultValue: "Лид",
                                          })
                                        : t("sales_deals", {
                                            defaultValue: "Сделка",
                                          })}
                                </span>
                                <h6>{item.title}</h6>
                              </div>

                              <time dateTime={item.date}>
                                {formatDate(item.date)}
                              </time>
                            </div>

                            {item.description && (
                              <p>{compactText(item.description, 180)}</p>
                            )}

                            <div className="pdTimelineEventFooter">
                              <span
                                className={`pdStatusBadge ${statusClass(
                                  item.status,
                                )}`}
                              >
                                {item.status
                                  ? t(item.status, {
                                      defaultValue: item.status,
                                    })
                                  : "--"}
                              </span>

                              {item.files.length > 0 && (
                                <span className="pdTimelineFilesCount">
                                  📎 {item.files.length}
                                </span>
                              )}

                              <button
                                type="button"
                                data-academy="timeline-event-open"
                                data-academy-entity-title={item.title || ""}
                                data-academy-event-kind={item.kind}
                                className="pdTimelineOpen"
                                onClick={() => openTimelineItem(item)}
                              >
                                {t("open", { defaultValue: "Открыть" })} →
                              </button>
                            </div>

                            {item.files.length > 0 && (
                              <div className="pdTimelineFileStrip">
                                {item.files.slice(0, 4).map((file) => {
                                  const image = String(
                                    file.mime_type || "",
                                  ).startsWith("image/");
                                  const url =
                                    file.web_view_link ||
                                    file.web_content_link ||
                                    file.thumbnail_link ||
                                    "#";

                                  return (
                                    <a
                                      key={file.id}
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="pdTimelineFile"
                                      title={file.file_name || "file"}
                                    >
                                      {image && file.thumbnail_link ? (
                                        <img
                                          src={file.thumbnail_link}
                                          alt={file.file_name || "file"}
                                        />
                                      ) : (
                                        <span>{image ? "🖼" : "📄"}</span>
                                      )}
                                    </a>
                                  );
                                })}

                                {item.files.length > 4 && (
                                  <span className="pdTimelineMoreFiles">
                                    +{item.files.length - 4}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </section>
        )}

        {topTab === "documents" && (
          <section className="pdDocumentsWorkspace">
            <div className="pdDocumentsHeader">
              <div>
                <h5>{t("documents", { defaultValue: "Файлы и документы" })}</h5>
                <p>
                  {t("contact_documents_hint", {
                    defaultValue:
                      "Все фотографии, документы, чеки и вложения клиента в одном месте.",
                  })}
                </p>
              </div>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() =>
                  navigate(
                    `/menu/sales/activities?contact_id=${contactId}&create=1`,
                  )
                }
              >
                + {t("add", { defaultValue: "Добавить" })}
              </button>
            </div>

            <div className="pdDocumentsStats">
              <div>
                <span>{t("all_files", { defaultValue: "Всего файлов" })}</span>
                <b>{allContactFiles.length}</b>
              </div>
              <div>
                <span>{t("photos", { defaultValue: "Фотографии" })}</span>
                <b>{imageFiles.length}</b>
              </div>
              <div>
                <span>{t("documents", { defaultValue: "Документы" })}</span>
                <b>{documentFiles.length}</b>
              </div>
            </div>

            {allContactFiles.length === 0 ? (
              <div className="pdDocumentsEmpty">
                <strong>
                  {t("no_files", { defaultValue: "Файлов пока нет" })}
                </strong>
                <span>
                  {t("contact_documents_empty_hint", {
                    defaultValue:
                      "Добавьте фотографию или документ через задачу или активность.",
                  })}
                </span>
              </div>
            ) : (
              <>
                {imageFiles.length > 0 && (
                  <div className="pdDocumentsSection">
                    <div className="pdDocumentsSectionTitle">
                      {t("photos", { defaultValue: "Фотографии" })}
                    </div>

                    <div className="pdDocumentsImageGrid">
                      {imageFiles.map((file) => {
                        const url =
                          file.web_view_link ||
                          file.web_content_link ||
                          file.thumbnail_link ||
                          file.url ||
                          file.download_url ||
                          "#";

                        return (
                          <a
                            key={`image-${file.id}`}
                            className="pdDocumentImageCard"
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <div className="pdDocumentImagePreview">
                              {file.thumbnail_link ? (
                                <img
                                  src={file.thumbnail_link}
                                  alt={file.file_name || "image"}
                                />
                              ) : (
                                <span>🖼</span>
                              )}
                            </div>

                            <div className="pdDocumentCardInfo">
                              <b>{file.file_name || "Image"}</b>
                              <small>
                                {file.source_title || "--"} ·{" "}
                                {formatDateShort(
                                  file.source_date || file.created_at,
                                )}
                              </small>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {documentFiles.length > 0 && (
                  <div className="pdDocumentsSection">
                    <div className="pdDocumentsSectionTitle">
                      {t("documents", { defaultValue: "Документы" })}
                    </div>

                    <div className="pdDocumentList">
                      {documentFiles.map((file) => {
                        const url =
                          file.web_view_link ||
                          file.web_content_link ||
                          file.url ||
                          file.download_url ||
                          "#";

                        return (
                          <a
                            key={`document-${file.id}`}
                            className="pdDocumentRow"
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <span className="pdDocumentIcon">📄</span>
                            <span className="pdDocumentMain">
                              <b>{file.file_name || "Document"}</b>
                              <small>
                                {file.source_title || "--"} ·{" "}
                                {formatDate(
                                  file.source_date || file.created_at,
                                )}
                              </small>
                            </span>
                            <span className="pdDocumentType">
                              {file.mime_type || "file"}
                            </span>
                            <span className="pdDocumentOpen">↗</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {topTab === "revenue" && (
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{t("contact_revenue_title")}</h6>
              <span className="text-muted small">
                {t("contact_revenue_stats", {
                  deals: dealsCount,
                  open: openDeals.length,
                  won: wonDeals.length,
                })}
              </span>
            </div>
            <div className="card-body">
              {dealsLoading && (
                <div className="text-muted small">{t("loading")}</div>
              )}
              {!dealsLoading && dealsCount === 0 && (
                <div className="text-muted small">
                  {t("contact_no_revenue")}
                </div>
              )}
              {!dealsLoading && dealsCount > 0 && (
                <>
                  <div className="mb-3">
                    <strong>{t("contact_total_amount")}:</strong>{" "}
                    {totalAmount ? `${totalAmount} €` : "--"}
                  </div>
                  <div className="table-scroll">
                    <table className="table table-hover mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>{t("contact_deal")}</th>
                          <th>{t("status")}</th>
                          <th>{t("contact_amount")}</th>
                          <th>{t("contact_stage")}</th>
                          <th>{t("created_at")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deals.map((d) => (
                          <tr key={d.id}>
                            <td>
                              <button
                                type="button"
                                className="pdInlineLink"
                                onClick={() => openContactDeal(d)}
                              >
                                {d.title || `${t("contact_deal")} #${d.id}`}
                              </button>
                            </td>
                            <td>{d.status ? t(d.status, d.status) : "--"}</td>
                            <td>{d.amount ? `${d.amount} €` : "--"}</td>
                            <td>{d.stage?.name || "--"}</td>
                            <td>{formatDateShort(d.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {topTab === "intelligence" && (
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">{t("contact_intelligence_title")}</h6>
            </div>

            <div className="card-body">
              <div className="mb-3 d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    intelTab === "leadScore"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setIntelTab("leadScore")}
                >
                  {t("contact_lead_score")}
                </button>

                <button
                  type="button"
                  className={`btn btn-sm ${
                    intelTab === "nextStep"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setIntelTab("nextStep")}
                >
                  {t("contact_next_step")}
                </button>

                <button
                  type="button"
                  className={`btn btn-sm ${
                    intelTab === "activity"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setIntelTab("activity")}
                >
                  {t("contact_activity_analysis")}
                </button>
              </div>

              {intelTab === "leadScore" && (
                <>
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      {t("contact_lead_score_hint")}
                    </div>

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleGenerateLeadScore(true)}
                      disabled={aiLeadScoreLoading}
                    >
                      {aiLeadScoreLoading
                        ? t("contact_refreshing")
                        : t("refresh")}
                    </button>
                  </div>

                  {aiLeadScoreError && (
                    <div className="alert alert-danger py-2 mb-3">
                      {aiLeadScoreError}
                    </div>
                  )}

                  {aiLeadScoreLoading && !aiLeadScoreData && (
                    <div className="text-muted small">
                      {t("contact_loading_lead_score")}
                    </div>
                  )}

                  {aiLeadScoreData && (
                    <div className="row g-3">
                      <div className="col-md-4">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_lead_score")}
                          </div>
                          <div className="fs-4 fw-semibold">
                            {aiLeadScoreData.score ?? 0}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_lead_level")}
                          </div>
                          <div className="text-capitalize">
                            <span
                              className={
                                aiLeadScoreData.level === "hot"
                                  ? "badge bg-danger"
                                  : aiLeadScoreData.level === "warm"
                                    ? "badge bg-warning"
                                    : "badge bg-secondary"
                              }
                            >
                              {aiLeadScoreData.level
                                ? t(
                                    aiLeadScoreData.level,
                                    aiLeadScoreData.level,
                                  )
                                : "--"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("account_provider")}
                          </div>
                          <div className="text-capitalize">
                            {aiLeadScoreData.provider || "--"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_reason")}
                          </div>
                          <div>{aiLeadScoreData.reason || "--"}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {intelTab === "nextStep" && (
                <>
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      {t("contact_next_step_hint")}
                    </div>

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleGenerateNextStep(true)}
                      disabled={aiNextStepLoading}
                    >
                      {aiNextStepLoading
                        ? t("contact_refreshing")
                        : t("refresh")}
                    </button>
                  </div>

                  {aiNextStepError && (
                    <div className="alert alert-danger py-2 mb-3">
                      {aiNextStepError}
                    </div>
                  )}

                  {aiNextStepLoading && !aiNextStepData && (
                    <div className="text-muted small">
                      {t("contact_loading_next_step")}
                    </div>
                  )}

                  {aiNextStepData && (
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_recommended_next_step")}
                          </div>
                          <div>{aiNextStepData.next_step || "--"}</div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_risk_level")}
                          </div>
                          <div className="text-capitalize">
                            <span
                              className={
                                aiNextStepData.risk_level === "high"
                                  ? "badge bg-danger"
                                  : aiNextStepData.risk_level === "medium"
                                    ? "badge bg-warning"
                                    : "badge bg-success"
                              }
                            >
                              {aiNextStepData.risk_level
                                ? t(
                                    aiNextStepData.risk_level,
                                    aiNextStepData.risk_level,
                                  )
                                : "--"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_reason")}
                          </div>
                          <div>{aiNextStepData.reason || "--"}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {intelTab === "activity" && (
                <>
                  <div className="mb-3 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                      {t("contact_activity_analysis_hint")}
                    </div>

                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleAnalyzeActivity(true)}
                      disabled={activityAnalysisLoading}
                    >
                      {activityAnalysisLoading
                        ? t("contact_refreshing")
                        : t("refresh")}
                    </button>
                  </div>

                  {activityAnalysisError && (
                    <div className="alert alert-danger py-2 mb-3">
                      {activityAnalysisError}
                    </div>
                  )}

                  {activityAnalysisLoading && !activityAnalysis && (
                    <div className="text-muted small">
                      {t("contact_loading_activity_analysis")}
                    </div>
                  )}

                  {activityAnalysis && (
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_activity_provider")}
                          </div>
                          <div className="text-capitalize">
                            {activityAnalysis.provider || "--"}
                          </div>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_activity_mode")}
                          </div>
                          <div className="text-capitalize">
                            {activityAnalysis.mode || "--"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-2">
                            {t("contact_insights")}
                          </div>

                          {Array.isArray(activityAnalysis.insights) &&
                          activityAnalysis.insights.length > 0 ? (
                            <ul className="mb-0 ps-3">
                              {activityAnalysis.insights.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <div>--</div>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div
                          className="p-3 rounded"
                          style={{
                            background: "var(--panel-2)",
                            border: "1px solid var(--outline)",
                          }}
                        >
                          <div className="small text-muted mb-1">
                            {t("contact_activity_recommendation")}
                          </div>
                          <div>{activityAnalysis.recommendation || "--"}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {!aiLeadScoreData &&
                !aiNextStepData &&
                !activityAnalysis &&
                !aiLeadScoreLoading &&
                !aiNextStepLoading &&
                !activityAnalysisLoading && (
                  <p className="text-muted mb-0">
                    {t("contact_generate_intelligence_hint")}
                  </p>
                )}
            </div>
          </div>
        )}

        <div className="mt-3">
          <Link
            to="/menu/sales/contacts"
            className="btn btn-link p-0"
            style={{ color: "var(--accent)" }}
          >
            ← {t("contact_back_to_contacts")}
          </Link>
        </div>
      </div>

      {toast && (
        <div
          className={`pdToast pdToast--${toast.tone || "success"}`}
          role="status"
        >
          <i
            className={
              toast.tone === "danger"
                ? "lni lni-warning"
                : toast.tone === "info"
                  ? "lni lni-information"
                  : "lni lni-checkmark-circle"
            }
          />
          <span>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}>
            ×
          </button>
        </div>
      )}

      {telegramEditOpen && (
        <>
          <div
            className="pdTelegramEditBackdrop"
            onMouseDown={closeTelegramEditor}
          />
          <div
            className="pdTelegramEditOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pd-telegram-edit-title"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeTelegramEditor();
            }}
          >
            <form
              className="pdTelegramEditModal"
              onSubmit={(event) => {
                event.preventDefault();
                saveTelegramUsername();
              }}
            >
              <header className="pdTelegramEditHeader">
                <span className="pdTelegramEditIcon">
                  <i className="lni lni-telegram" />
                </span>
                <div>
                  <small>{t("contact", { defaultValue: "Контакт" })}</small>
                  <h5 id="pd-telegram-edit-title">
                    {t("edit_telegram", { defaultValue: "Изменить Telegram" })}
                  </h5>
                </div>
                <button
                  type="button"
                  className="pdTelegramEditClose"
                  onClick={closeTelegramEditor}
                  disabled={telegramSaving}
                  aria-label={t("close", { defaultValue: "Закрыть" })}
                >
                  ×
                </button>
              </header>

              <div className="pdTelegramEditBody">
                <label htmlFor="pd-telegram-username">{t("source_preset_telegram")}</label>
                <div className="pdTelegramEditControl">
                  <span aria-hidden="true">@</span>
                  <input
                    id="pd-telegram-username"
                    type="text"
                    autoFocus
                    autoComplete="off"
                    maxLength="32"
                    value={telegramDraft}
                    onChange={(event) => {
                      setTelegramDraft(
                        normalizeTelegramUsername(event.target.value),
                      );
                      if (telegramEditError) setTelegramEditError("");
                    }}
                    placeholder="username"
                    aria-describedby="pd-telegram-edit-hint"
                  />
                </div>
                <p id="pd-telegram-edit-hint">
                  {t("telegram_username_hint", {
                    defaultValue:
                      "Введите username без @. Оставьте поле пустым, чтобы удалить значение.",
                  })}
                </p>
                {telegramEditError && (
                  <div className="pdTelegramEditError" role="alert">
                    {telegramEditError}
                  </div>
                )}
              </div>

              <footer className="pdTelegramEditFooter">
                <button
                  type="button"
                  className="pdTelegramEditCancel"
                  onClick={closeTelegramEditor}
                  disabled={telegramSaving}
                >
                  {t("cancel", { defaultValue: "Отмена" })}
                </button>
                <button
                  type="submit"
                  className="pdTelegramEditSave"
                  disabled={telegramSaving}
                >
                  {telegramSaving && <span className="pdTelegramEditSpinner" />}
                  {telegramSaving
                    ? t("saving", { defaultValue: "Сохранение..." })
                    : t("save", { defaultValue: "Сохранить" })}
                </button>
              </footer>
            </form>
          </div>
        </>
      )}

      <div className={`pdMobileFab ${mobileActionsOpen ? "is-open" : ""}`}>
        {mobileActionsOpen && (
          <button
            type="button"
            className="pdMobileFabBackdrop"
            aria-label={t("close")}
            onClick={() => setMobileActionsOpen(false)}
          />
        )}
        <div className="pdMobileFabMenu">
          <button type="button" onClick={() => openQuickCreate("task")}>
            <i className="lni lni-checkmark-circle" />
            <span>{t("contact_new_task", { defaultValue: "Задача" })}</span>
          </button>
          <button type="button" onClick={() => openQuickCreate("lead")}>
            <i className="lni lni-target" />
            <span>{t("create_lead", { defaultValue: "Лид" })}</span>
          </button>
          <button type="button" onClick={() => openQuickCreate("deal")}>
            <i className="lni lni-briefcase-2" />
            <span>{t("contact_deal", { defaultValue: "Сделка" })}</span>
          </button>
          {selectedWork && (
            <button
              type="button"
              onClick={() => {
                setMobileActionsOpen(false);
                openPaymentModal();
              }}
            >
              <i className="lni lni-wallet" />
              <span>{t("add_payment", { defaultValue: "Оплата" })}</span>
            </button>
          )}
          {selectedWork && (
            <button
              type="button"
              onClick={() => {
                setMobileActionsOpen(false);
                workPhotoInputRef.current?.click();
              }}
            >
              <i className="lni lni-camera" />
              <span>{t("add_photo", { defaultValue: "Фото" })}</span>
            </button>
          )}
        </div>
        <button
          type="button"
          className="pdMobileFabMain"
          aria-expanded={mobileActionsOpen}
          onClick={() => setMobileActionsOpen((value) => !value)}
        >
          {mobileActionsOpen ? "×" : "+"}
        </button>
      </div>

      {quickCreateModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeQuickCreate}
          />
          <div
            data-academy={`${quickCreateModal}-create-modal`}
            className="modal fade show pdUxModal"
            tabIndex="-1"
            style={{ display: "block" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <form
                className="modal-content"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleQuickCreate();
                }}
                onKeyDown={(event) => {
                  if (
                    (event.ctrlKey || event.metaKey) &&
                    event.key === "Enter"
                  ) {
                    event.preventDefault();
                    handleQuickCreate();
                  }
                }}
              >
                <div className="modal-header">
                  <div>
                    <span className="pdUxModalEyebrow">
                      {t("quick_action", { defaultValue: "Быстрое действие" })}
                    </span>
                    <h5 className="modal-title">
                      {quickCreateModal === "lead"
                        ? t("create_lead", { defaultValue: "Новый лид" })
                        : quickCreateModal === "deal"
                          ? t("create_deal", { defaultValue: "Новая сделка" })
                          : t("create_task", { defaultValue: "Новая задача" })}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeQuickCreate}
                  />
                </div>
                <div className="modal-body">
                  <label className="form-label" htmlFor="quick-create-title">
                    {t("title", { defaultValue: "Название" })}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    id="quick-create-title"
                    className="form-control"
                    value={quickCreateTitle}
                    onChange={(event) =>
                      setQuickCreateTitle(event.target.value)
                    }
                    maxLength="255"
                    autoFocus
                    required
                  />
                  {quickCreateModal === "lead" && (
                    <>
                      <label
                        className="form-label mt-3"
                        htmlFor="quick-create-source"
                      >
                        {t("lead_source", { defaultValue: "Источник" })}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <select
                        id="quick-create-source"
                        className="form-select"
                        value={quickCreateSourceId}
                        onChange={(event) =>
                          setQuickCreateSourceId(event.target.value)
                        }
                        disabled={quickCreateSourcesLoading}
                        required
                      >
                        <option value="">
                          {quickCreateSourcesLoading
                            ? `${t("loading", { defaultValue: "Загрузка" })}...`
                            : t("select_source", {
                                defaultValue: "Выберите источник",
                              })}
                        </option>
                        {quickCreateSources.map((source) => (
                          <option key={source.id} value={source.id}>
                            {t(source.name, { defaultValue: source.name })}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  {quickCreateModal === "deal" && (
                    <>
                      <label
                        className="form-label mt-3"
                        htmlFor="quick-create-amount"
                      >
                        {t("amount", { defaultValue: "Сумма" })}
                      </label>
                      <div className="input-group">
                        <input
                          id="quick-create-amount"
                          className="form-control"
                          type="number"
                          min="0"
                          step="0.01"
                          value={quickCreateAmount}
                          onChange={(event) =>
                            setQuickCreateAmount(event.target.value)
                          }
                          placeholder="0.00"
                        />
                        <span className="input-group-text">€</span>
                      </div>
                    </>
                  )}
                  {quickCreateError && (
                    <div className="pdUxFormError">
                      {String(quickCreateError)}
                    </div>
                  )}
                  <div className="pdUxShortcutHint">
                    <kbd>Ctrl</kbd>
                    <span>+</span>
                    <kbd>Enter</kbd>
                    <span>{t("save", { defaultValue: "Сохранить" })}</span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={quickCreateSaving}
                    onClick={closeQuickCreate}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    data-academy={`${quickCreateModal}-create-submit`}
                    className="btn btn-primary"
                    type="submit"
                    disabled={
                      quickCreateSaving ||
                      !quickCreateTitle.trim() ||
                      (quickCreateModal === "lead" && !quickCreateSourceId)
                    }
                  >
                    {quickCreateSaving
                      ? t("saving", { defaultValue: "Сохранение..." })
                      : t("create", { defaultValue: "Создать" })}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {confirmAction && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => setConfirmAction(null)}
          />
          <div
            className="modal fade show pdUxModal pdConfirmModal"
            tabIndex="-1"
            style={{ display: "block" }}
            role="alertdialog"
            aria-modal="true"
          >
            <div
              className="modal-dialog modal-dialog-centered modal-sm"
              role="document"
            >
              <div className="modal-content">
                <div className="modal-body">
                  <div
                    className={`pdConfirmIcon ${confirmAction.danger ? "is-danger" : ""}`}
                  >
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      transform="rotate(0 0 0)"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M5.51953 5.18805C5.51953 3.4955 6.8916 2.12305 8.58432 2.12305C10.277 2.12305 11.6491 3.4955 11.6491 5.18804C11.6491 6.88059 10.277 8.25305 8.58432 8.25305C6.8916 8.25305 5.51953 6.8806 5.51953 5.18805ZM8.58432 3.62305C7.7202 3.62305 7.01953 4.32375 7.01953 5.18805C7.01953 6.05235 7.7202 6.75305 8.58432 6.75305C9.44844 6.75305 10.1491 6.05234 10.1491 5.18805C10.1491 4.32375 9.44844 3.62305 8.58432 3.62305Z"
                        fill="#ffec0aa1"
                      ></path>
                      <path
                        d="M2.83203 11.9632C2.83203 10.7207 3.83929 9.71305 5.08203 9.71305H12.086C12.7209 9.71305 13.2944 9.97605 13.7034 10.399C13.2115 10.6303 12.7514 10.9181 12.3315 11.2542C12.2546 11.2275 12.172 11.213 12.086 11.213H5.08203C4.66792 11.213 4.33203 11.5489 4.33203 11.9632V14.7424C4.33203 15.0736 4.54932 15.3656 4.8666 15.4607L6.05471 15.817C6.34369 15.9037 6.55261 16.1551 6.58496 16.455L6.96219 19.9537C7.00326 20.3346 7.32478 20.6233 7.70787 20.6233H9.46018C9.84327 20.6233 10.1648 20.3346 10.2059 19.9537L10.279 19.2749C10.5518 19.9271 10.9222 20.5284 11.3719 21.0604C10.9727 21.7024 10.2613 22.1233 9.46018 22.1233H7.70787C6.55859 22.1233 5.59404 21.2571 5.47084 20.1145L5.14697 17.1108L4.43573 16.8975C3.4839 16.6121 2.83203 15.7361 2.83203 14.7424V11.9632Z"
                        fill="#ffec0aa1"
                      ></path>
                      <path
                        d="M16.625 13.7469C17.0392 13.7469 17.375 14.0827 17.375 14.4969V16.2452C17.375 16.6594 17.0392 16.9952 16.625 16.9952C16.2108 16.9952 15.875 16.6594 15.875 16.2452V14.4969C15.875 14.0827 16.2108 13.7469 16.625 13.7469Z"
                        fill="#ffec0aa1"
                      ></path>
                      <path
                        d="M15.824 18.7473C15.824 18.3055 16.1822 17.9473 16.624 17.9473C17.0658 17.9473 17.425 18.3055 17.425 18.7473C17.425 19.1892 17.0668 19.5473 16.625 19.5473C16.1832 19.5473 15.824 19.1892 15.824 18.7473Z"
                        fill="#ffec0aa1"
                      ></path>
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M11.2495 16.6245C11.2495 13.656 13.656 11.2495 16.6245 11.2495C19.593 11.2495 21.9995 13.656 21.9995 16.6245C21.9995 19.593 19.593 21.9995 16.6245 21.9995C13.656 21.9995 11.2495 19.593 11.2495 16.6245ZM16.6245 12.7495C14.4844 12.7495 12.7495 14.4844 12.7495 16.6245C12.7495 18.7646 14.4844 20.4995 16.6245 20.4995C18.7646 20.4995 20.4995 18.7646 20.4995 16.6245C20.4995 14.4844 18.7646 12.7495 16.6245 12.7495Z"
                        fill="#ffec0aa1"
                      ></path>
                    </svg>
                  </div>
                  <h5>{confirmAction.title}</h5>
                  <p className="pdConfirmMessage">{confirmAction.message}</p>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setConfirmAction(null)}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    className={
                      confirmAction.danger
                        ? "btn btn-danger"
                        : "btn btn-primary"
                    }
                    type="button"
                    onClick={() =>
                      confirmAction.type === "delete-contact"
                        ? confirmDeleteContact()
                        : confirmAction.type === "delete-photo"
                          ? confirmDeleteWorkPhoto(confirmAction.file)
                          : setConfirmAction(null)
                    }
                  >
                    {t("confirm", { defaultValue: "Подтвердить" })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {workPhotoLightbox && (
        <div
          className="pdWorkPhotoModal"
          role="dialog"
          aria-modal="true"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setWorkPhotoLightbox(null);
          }}
        >
          <div className="pdWorkPhotoModalCard">
            <header>
              <div>
                <span>{workPhotoLightbox.group}</span>
                <b>
                  {String(workPhotoLightbox.file_name || "photo").replace(
                    /^((before|during|after)__)+/i,
                    "",
                  )}
                </b>
              </div>
              <button type="button" onClick={() => setWorkPhotoLightbox(null)}>
                ×
              </button>
            </header>
            <div className="pdWorkPhotoModalImage">
              <img
                src={getWorkFileUrl(workPhotoLightbox)}
                alt={workPhotoLightbox.file_name || "photo"}
              />
            </div>
            <footer>
              <a
                href={getWorkFileUrl(workPhotoLightbox)}
                target="_blank"
                rel="noreferrer"
              >
                {t("open_original", { defaultValue: "Открыть оригинал" })}
              </a>
              <button
                type="button"
                onClick={() => deleteWorkPhoto(workPhotoLightbox)}
              >
                {t("delete", { defaultValue: "Удалить" })}
              </button>
            </footer>
          </div>
        </div>
      )}
      {workPhotoCompareOpen && (
        <div
          className="pdWorkPhotoModal"
          role="dialog"
          aria-modal="true"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setWorkPhotoCompareOpen(false);
          }}
        >
          <div className="pdWorkCompareModalCard">
            <header>
              <div>
                <span>
                  {t("photo_comparison", {
                    defaultValue: "Сравнение результата",
                  })}
                </span>
                <b>{t("before_after", { defaultValue: "До / После" })}</b>
              </div>
              <button
                type="button"
                onClick={() => setWorkPhotoCompareOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="pdWorkCompareStage">
              <img
                className="pdWorkCompareBefore"
                src={getWorkFileUrl(
                  selectedWorkFileGroups.before.find((file) =>
                    String(file.mime_type || "").startsWith("image/"),
                  ),
                )}
                              alt={t("personal_before_alt")}
              />
              <div
                className="pdWorkCompareAfterWrap"
                style={{ width: `${workPhotoComparePosition}%` }}
              >
                <img
                  className="pdWorkCompareAfter"
                  src={getWorkFileUrl(
                    selectedWorkFileGroups.after.find((file) =>
                      String(file.mime_type || "").startsWith("image/"),
                    ),
                  )}
                              alt={t("personal_after_alt")}
                />
              </div>
              <div
                className="pdWorkCompareDivider"
                style={{ left: `${workPhotoComparePosition}%` }}
              >
                <span>↔</span>
              </div>
              <span className="pdWorkCompareLabel pdWorkCompareLabel--before">
                {t("before", { defaultValue: "До" })}
              </span>
              <span className="pdWorkCompareLabel pdWorkCompareLabel--after">
                {t("after", { defaultValue: "После" })}
              </span>
            </div>
            <input
              className="pdWorkCompareRange"
              type="range"
              min="0"
              max="100"
              value={workPhotoComparePosition}
              onChange={(event) =>
                setWorkPhotoComparePosition(Number(event.target.value))
              }
            />
          </div>
        </div>
      )}

      {showPaymentModal && selectedWork && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closePaymentModal}
          />
          <div
            className="modal fade show pdPaymentModal"
            tabIndex="-1"
            style={{ display: "block" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <span className="pdCompleteEyebrow">
                      {t("payment", { defaultValue: "Финансы работы" })}
                    </span>
                    <h5 className="modal-title">
                      {t("add_payment", { defaultValue: "Добавить оплату" })}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closePaymentModal}
                  />
                </div>
                <div className="modal-body">
                  <div className="pdPaymentModalSummary">
                    <div>
                      <span>{t("amount", { defaultValue: "Стоимость" })}</span>
                      <b>{selectedWorkAmount.toFixed(2)} €</b>
                    </div>
                    <div>
                      <span>{t("paid", { defaultValue: "Оплачено" })}</span>
                      <b>{selectedWorkPaid.toFixed(2)} €</b>
                    </div>
                    <div>
                      <span>{t("balance", { defaultValue: "Остаток" })}</span>
                      <b>{selectedWorkBalance.toFixed(2)} €</b>
                    </div>
                  </div>
                  <label className="form-label mt-3" htmlFor="payment-amount">
                    {t("payment_amount", { defaultValue: "Сумма оплаты" })}
                  </label>
                  <div className="input-group">
                    <input
                      id="payment-amount"
                      className="form-control"
                      type="number"
                      min="0.01"
                      step="0.01"
                      max={selectedWorkBalance || undefined}
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder="0.00"
                      autoFocus
                    />
                    <span className="input-group-text">€</span>
                  </div>
                  <label className="form-label mt-3" htmlFor="payment-method">
                    {t("payment_method", { defaultValue: "Способ оплаты" })}
                  </label>
                  <select
                    id="payment-method"
                    className="form-select"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  >
                    <option value="cash">
                      {t("cash", { defaultValue: "Наличные" })}
                    </option>
                    <option value="card">
                      {t("card", { defaultValue: "Карта" })}
                    </option>
                    <option value="bank_transfer">
                      {t("bank_transfer", {
                        defaultValue: "Банковский перевод",
                      })}
                    </option>
                    <option value="other">
                      {t("other", { defaultValue: "Другое" })}
                    </option>
                  </select>
                  <label className="form-label mt-3" htmlFor="payment-note">
                    {t("note", { defaultValue: "Комментарий" })}
                  </label>
                  <textarea
                    id="payment-note"
                    className="form-control"
                    rows="3"
                    maxLength="500"
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    placeholder={t("payment_note_placeholder", {
                      defaultValue:
                        "Например: предоплата, окончательный расчёт, номер чека.",
                    })}
                  />
                  {paymentError && (
                    <div className="text-danger small mt-2">
                      {String(paymentError)}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={paymentSaving}
                    onClick={closePaymentModal}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={paymentSaving || !paymentAmount}
                    onClick={handleAddPayment}
                  >
                    {paymentSaving
                      ? t("saving", { defaultValue: "Сохранение..." })
                      : t("save_payment", { defaultValue: "Сохранить оплату" })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showCompleteWorkModal && selectedWork && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeCompleteWork}
          />
          <div
            className="modal fade show pdCompleteWorkModal"
            tabIndex="-1"
            style={{ display: "block" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <span className="pdCompleteEyebrow">
                      {t("complete_work", {
                        defaultValue: "Завершение работы",
                      })}
                    </span>
                    <h5 className="modal-title">{selectedWork.title}</h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeCompleteWork}
                  />
                </div>
                <div className="modal-body">
                  <label className="form-label">
                    {t("work_result", { defaultValue: "Результат" })}
                  </label>
                  <div className="pdCompletionStatusChoice">
                    <button
                      type="button"
                      className={
                        completeWorkStatus === "won"
                          ? "is-active is-success"
                          : ""
                      }
                      onClick={() => setCompleteWorkStatus("won")}
                    >
                      ✓ {t("won", { defaultValue: "Выполнено" })}
                    </button>
                    <button
                      type="button"
                      className={
                        completeWorkStatus === "lost"
                          ? "is-active is-danger"
                          : ""
                      }
                      onClick={() => setCompleteWorkStatus("lost")}
                    >
                      × {t("lost", { defaultValue: "Отменено" })}
                    </button>
                  </div>
                  <label className="form-label mt-3" htmlFor="work-summary">
                    {t("work_summary", { defaultValue: "Что было сделано" })}
                  </label>
                  <textarea
                    id="work-summary"
                    className="form-control"
                    rows="5"
                    maxLength="5000"
                    value={completeWorkSummary}
                    onChange={(e) => setCompleteWorkSummary(e.target.value)}
                    placeholder={t("work_summary_placeholder", {
                      defaultValue:
                        "Например: заменён экран, очищен разъём, устройство протестировано и выдано клиенту.",
                    })}
                  />
                  <div className="pdCompleteHint">
                    {t("work_archive_hint", {
                      defaultValue:
                        "Дата, сумма, задачи и фотографии сохранятся в архиве автоматически.",
                    })}
                  </div>
                  {completeWorkError && (
                    <div className="text-danger small mt-2">
                      {String(completeWorkError)}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={completeWorkSaving}
                    onClick={closeCompleteWork}
                  >
                    {t("cancel")}
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={
                      completeWorkSaving ||
                      completeWorkSummary.trim().length < 3
                    }
                    onClick={handleCompleteWork}
                  >
                    {completeWorkSaving
                      ? t("saving", { defaultValue: "Сохранение..." })
                      : t("complete_and_archive", {
                          defaultValue: "Завершить и сохранить",
                        })}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showCompanyModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeCompanyModal}
          />

          <div
            className="modal fade show"
            tabIndex="-1"
            style={{ display: "block" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div
                className="modal-content"
                style={{
                  background: "var(--panel)",
                  borderColor: "var(--outline)",
                }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">
                    {t("contact_assign_company_title")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeCompanyModal}
                  />
                </div>

                <div className="modal-body">
                  {companiesError && (
                    <div className="alert alert-danger py-2">
                      {companiesError}
                    </div>
                  )}

                  <label className="form-label text-muted">
                    {t("sales_contacts_company")}
                  </label>

                  {companiesLoading ? (
                    <div className="text-muted small">
                      {t("contact_loading_companies")}
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                    >
                      <option value="">
                        {t("contact_select_company_option")}
                      </option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.firm_name} ({c.email})
                        </option>
                      ))}
                    </select>
                  )}

                  {assignError && (
                    <div className="text-danger small mt-2">{assignError}</div>
                  )}

                  <div className="mt-3 text-muted small">
                    {t("contact_tip_create_company_inline")}
                  </div>
                </div>

                <div className="modal-footer">
                  {company && (
                    <button
                      className="btn btn-outline-danger"
                      type="button"
                      disabled={assignLoading}
                      onClick={handleUnassignCompany}
                    >
                      {t("remove")}
                    </button>
                  )}

                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    disabled={assignLoading}
                    onClick={closeCompanyModal}
                  >
                    {t("cancel")}
                  </button>

                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={assignLoading || !selectedCompanyId}
                    onClick={handleAssignCompany}
                  >
                    {assignLoading
                      ? t("contact_assigning")
                      : t("contact_assign")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
